import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Save, Plus, Trash2, Upload, Check, X, FileText, LayoutTemplate, Edit3, ChevronUp, ChevronDown, ChevronLeft, Sparkles, Maximize2, AlertTriangle, ChevronRight, Folder, MoreHorizontal, HelpCircle, Tag, RefreshCw, Download, PanelLeftClose, PanelLeftOpen, Copy } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableRowWrapper, DragHandle } from './SortableRow';
import OCRLabelingModal from './OCRLabelingModal';
import { getDefaultDescription } from '../utils/fieldDefaults';

export default function TemplateManagement({
    onClose,
    isModal = true, // 모달 모드 여부 (false면 사이드 드로어 모드)
    selectedFile = null, // 현재 선택된 파일 (헤더 추출용)
    currentDocumentImage = null, // 🏷️ 현재 문서 이미지 (base64 PNG) - 라벨링용
    onExtractFromCurrent = null, // 현재 파일에서 추출 함수
    apiBaseUrl = 'http://localhost:3002', // API 기본 URL
    onUpdate = null, // 템플릿 변경 시 호출될 콜백
    onUpdateViewer = null, // 🚀 메인 뷰어 업데이트 함수
    onTemplateSelect = null, // 🎯 템플릿 선택 시 App.js에 알림
    initialSelectedId = null, // 초기 선택 템플릿 ID (없을 수 있음)
    initialMode = 'list', // 'list' | 'create'
    initialActiveTab = 'fields' // 🔧 초기 활성 탭 ('fields' | 'prompt' | 'mergeRules')
}) {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);

    // 편집 중인 템플릿 상태
    const [editName, setEditName] = useState('');
    const [editDocType, setEditDocType] = useState('');
    const [editFields, setEditFields] = useState([]); // 단일 필드 (Single Fields)
    const [editTableFields, setEditTableFields] = useState([]); // 🔧 테이블 필드 그룹 (Table Fields)
    const [editComputedFields, setEditComputedFields] = useState([]); // 🔧 합성 필드 (Computed Fields)
    const [editPrimaryKey, setEditPrimaryKey] = useState('');
    const [editCategory, setEditCategory] = useState("공통 (General)"); // 카테고리 상태 (기본값)
    const [exampleValues, setExampleValues] = useState({}); // 예시 값 저장
    const [customPrompt, setCustomPrompt] = useState(''); // 사용자 정의 프롬프트
    const [systemPrompt, setSystemPrompt] = useState(''); // 🆕 시스템 프롬프트 (기본 규칙)
    const [previewPrompt, setPreviewPrompt] = useState(''); // 자동 생성 프롬프트 미리보기
    const [activeTab, setActiveTab] = useState(initialActiveTab); // 'fields' | 'prompt' | 'mergeRules'
    const [editMergeRules, setEditMergeRules] = useState([]); // 📄 머지 규칙 편집
    const fileInputRef = useRef(null); // 파일 입력 ref
    const [selectedFields, setSelectedFields] = useState([]); // 선택된 필드 키 배열
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null); // Shift-click을 위한 마지막 선택 인덱스
    const [selectedTableColumns, setSelectedTableColumns] = useState([]); // 테이블 컬럼 선택 상태 (배열)
    const [isPopupOpen, setIsPopupOpen] = useState(false); // 팝업 열림 상태
    const [isMetaPromptPopupOpen, setIsMetaPromptPopupOpen] = useState(false); // Meta-Prompt 팝업 열림 상태
    const [isRefining, setIsRefining] = useState(false); // 프롬프트 수정 중 상태
    const [isExtracting, setIsExtracting] = useState(false); // 헤더 추출 중 상태
    const [toast, setToast] = useState({ show: false, message: '' }); // Toast 알림 상태
    const [sampleFileToSave, setSampleFileToSave] = useState(null); // 샘플 파일 저장용
    const [lastSelectedFieldKey, setLastSelectedFieldKey] = useState(null); // Shift-click을 위한 마지막 선택 키
    const [isGenerating, setIsGenerating] = useState(false); // 자동 생성 중 상태
    const [refinementInstructions, setRefinementInstructions] = useState(''); // 프롬프트 수정 지시사항
    const [metaPromptHeight, setMetaPromptHeight] = useState(300); // Meta-Prompt 영역 높이
    const [autoGenSource, setAutoGenSource] = useState(null); // Auto-generate 소스 ('upload' | 'current' | null)
    const [schemaLanguage, setSchemaLanguage] = useState('ko'); // 스키마 언어 ('ko' | 'en')

    // ⭐ 페이지 선택 모달 관련 state
    const [showPageSelectionModal, setShowPageSelectionModal] = useState(false);
    const [pdfPageThumbnails, setPdfPageThumbnails] = useState([]); // 페이지 썸네일 배열
    const [selectedPages, setSelectedPages] = useState([1]); // 선택된 페이지 번호 배열
    const [pendingFile, setPendingFile] = useState(null); // 분석 대기 중인 파일
    const [totalPdfPages, setTotalPdfPages] = useState(0); // PDF 총 페이지 수
    const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(false); // 썸네일 로딩 중
    const [showHelpModal, setShowHelpModal] = useState(false); // 도움말 모달
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // 사이드바 접힘 (기본: 접힘)
    const [showMergeHelpModal, setShowMergeHelpModal] = useState(false); // 📄 머지 규칙 도움말 모달
    const [mergeRolePopup, setMergeRolePopup] = useState({ show: false, fieldKey: null, x: 0, y: 0 }); // 머지 역할 팝업
    const [isEnhancingSystemPrompt, setIsEnhancingSystemPrompt] = useState(false); // 🆕 AI 가이드 중

    // 🔍 EXAMPLE 확대 편집 모달
    const [exampleExpandModal, setExampleExpandModal] = useState({ isOpen: false, fieldType: '', fieldKey: '', value: '', tableIdx: -1, colIdx: -1 });

    // ⭐ 라벨링 모달 관련 state
    const [showLabelingModal, setShowLabelingModal] = useState(false);
    const [labelingSampleImageUrl, setLabelingSampleImageUrl] = useState(null);
    const [labelingSampleFileName, setLabelingSampleFileName] = useState(null); // 🏷️ 파일명 (Overwrite 용)

    // 🔧 외부에서 initialActiveTab이 변경되면 activeTab 동기화
    useEffect(() => {
        if (initialActiveTab) {
            setActiveTab(initialActiveTab);
        }
    }, [initialActiveTab]);

    // 1. 템플릿 목록 로드 및 초기 모드 처리
    useEffect(() => {
        const init = async () => {
            await fetchTemplates();
        };
        init();
    }, []);

    // fetchTemplates 완료 후 또는 props 변경 시 초기 모드 진입
    useEffect(() => {
        if (templates.length > 0) {
            if (initialMode === 'create') {
                handleCreateNew();
            } else if (initialSelectedId) {
                const t = templates.find(t => t.id === initialSelectedId);
                if (t) selectTemplate(t);
            } else if (!selectedTemplateId && initialMode !== 'create') {
                // 기본값: 첫 번째 선택
                selectTemplate(templates[0]);
            }
        } else if (initialMode === 'create') {
            // 템플릿이 하나도 없을 때도 create 모드면 진입
            handleCreateNew();
        }
    }, [templates, initialMode, initialSelectedId]);

    // 🔑 Auth 헤더 생성 헬퍼
    const getAuthHeaders = () => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    };

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${apiBaseUrl}/api/templates`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();
            setTemplates(data);
            // Note: selection logic moved to separate useEffect
        } catch (error) {
            console.error('템플릿 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // 🚀 [Upstage Benchmark] Helper: Normalize fields to Schema Objects
    // 🔧 Fix: Add default outputOrder if missing for legacy templates
    const normalizeFields = (fields) => {
        if (!fields) return [];
        return fields.map((f, idx) => {
            if (typeof f === 'string') {
                return { key: f, type: 'string', description: '', outputOrder: idx + 1 };
            }
            return {
                ...f,
                type: f.type || 'string',
                description: f.description || '',
                // 🔧 Preserve existing outputOrder or assign default based on index
                outputOrder: f.outputOrder !== undefined ? f.outputOrder : (idx + 1)
            };
        });
    };

    // 🔄 Helper: 필드가 머지 규칙에서 어떤 역할인지 아이콘 반환
    const getMergeRoleIcons = (fieldKey) => {
        const icons = [];
        for (const rule of editMergeRules) {
            // Primary Key (새 primaryKeys 배열 또는 기존 mainIdentifier)
            const pkFields = rule.primaryKeys || (rule.mainIdentifier ? [rule.mainIdentifier] : []);
            for (const pk of pkFields) {
                if (pk && (pk === fieldKey || fieldKey.includes(pk) || pk.includes(fieldKey))) {
                    icons.push({ bgColor: 'bg-gray-600', title: 'Primary Key' });
                    break;
                }
            }
            // Match Key (색상을 초록으로 변경)
            if (rule.matchKey && (rule.matchKey === fieldKey || fieldKey.includes(rule.matchKey) || rule.matchKey.includes(fieldKey))) {
                icons.push({ bgColor: 'bg-green-500', title: 'Match Key' });
            }
            // 필드 매핑
            for (const mapping of (rule.fieldMappings || [])) {
                if (mapping.sourceField && (mapping.sourceField === fieldKey || fieldKey.includes(mapping.sourceField))) {
                    icons.push({ bgColor: 'bg-gray-400', title: `→ ${mapping.targetField}` });
                }
                if (mapping.targetField && (mapping.targetField === fieldKey || fieldKey.includes(mapping.targetField))) {
                    icons.push({ bgColor: 'bg-teal-500', title: `← ${mapping.sourceField}` });
                }
            }
        }
        // 중복 제거
        return icons.filter((v, i, a) => a.findIndex(t => t.bgColor === v.bgColor && t.title === v.title) === i);
    };

    // 🔄 Helper: 필드에 머지 역할 빠르게 지정
    const setFieldMergeRole = (fieldKey, role) => {
        setEditMergeRules(prevRules => {
            let rules = [...prevRules];
            if (rules.length === 0) {
                rules = [{ id: Date.now(), mainIdentifier: '', subIdentifier: '', matchKey: '', fieldMappings: [] }];
            }
            const rule = { ...rules[0] };
            // 기존 역할에서 제거
            if (rule.mainIdentifier) {
                rule.mainIdentifier = rule.mainIdentifier.split(',').map(f => f.trim()).filter(f => f !== fieldKey).join(', ');
            }
            if (rule.subIdentifier) {
                rule.subIdentifier = rule.subIdentifier.split(',').map(f => f.trim()).filter(f => f !== fieldKey).join(', ');
            }
            if (rule.matchKey === fieldKey) rule.matchKey = '';
            // 새 역할 추가
            if (role === 'main') rule.mainIdentifier = rule.mainIdentifier ? `${rule.mainIdentifier}, ${fieldKey}` : fieldKey;
            if (role === 'sub') rule.subIdentifier = rule.subIdentifier ? `${rule.subIdentifier}, ${fieldKey}` : fieldKey;
            if (role === 'match') rule.matchKey = fieldKey;
            rules[0] = rule;
            return rules;
        });
        setMergeRolePopup({ show: false, fieldKey: null, x: 0, y: 0 });
        setIsEditing(true);
    };

    // 🔄 Helper: 필드의 현재 머지 역할 가져오기
    const getFieldMergeRole = (fieldKey) => {
        for (const rule of editMergeRules) {
            if (rule.mainIdentifier?.split(',').map(f => f.trim()).includes(fieldKey)) return 'main';
            if (rule.subIdentifier?.split(',').map(f => f.trim()).includes(fieldKey)) return 'sub';
            if (rule.matchKey === fieldKey) return 'match';
        }
        return 'none';
    };

    // 🔧 Helper: Normalize table fields to ensure outputOrder exists
    const normalizeTableFields = (tableFields, singleFieldCount) => {
        if (!tableFields) return [];
        let baseOrder = singleFieldCount;
        return tableFields.map((table, tableIdx) => ({
            ...table,
            columns: (table.columns || []).map((col, colIdx) => ({
                ...col,
                outputOrder: col.outputOrder !== undefined ? col.outputOrder : (baseOrder + tableIdx * 100 + colIdx + 1)
            }))
        }));
    };

    // 🔧 Helper: Normalize computed fields to ensure outputOrder exists
    const normalizeComputedFields = (computedFields, singleFieldCount, tableColCount) => {
        if (!computedFields) return [];
        const baseOrder = singleFieldCount + tableColCount;
        return computedFields.map((cf, idx) => ({
            ...cf,
            outputOrder: cf.outputOrder !== undefined ? cf.outputOrder : (cf.order !== undefined ? cf.order : (baseOrder + idx + 1))
        }));
    };

    const selectTemplate = async (template) => {
        setSelectedTemplateId(template.id);
        setEditName(template.name);
        setEditDocType(template.documentType);
        // 🚀 Normalize fields on load - 단일 필드
        const singleFields = normalizeFields(template.fields || template.singleFields || []);
        // 🏷️ description이 비어있으면 DTO 기본값 자동 채우기
        singleFields.forEach(f => {
            if (typeof f === 'object' && !f.description) {
                f.description = getDefaultDescription(f.key);
            }
        });
        setEditFields(singleFields);
        // 🔧 테이블 필드 그룹 로드 (normalize with outputOrder)
        const tableFields = normalizeTableFields(template.tableFields || [], singleFields.length);
        // 🏷️ 테이블 컬럼 description 자동 채우기
        tableFields.forEach(t => {
            (t.columns || []).forEach(col => {
                if (typeof col === 'object' && !col.description) {
                    col.description = getDefaultDescription(col.key);
                }
            });
        });
        setEditTableFields(tableFields);
        // 🔧 합성 필드 로드 (normalize with outputOrder)
        const tableColCount = tableFields.reduce((sum, t) => sum + (t.columns?.length || 0), 0);
        const computedFields = normalizeComputedFields(template.computedFields || [], singleFields.length, tableColCount);
        setEditComputedFields(computedFields);
        setEditPrimaryKey(template.primaryKey || '');
        setEditCategory(template.category || "공통 (General)"); // 카테고리 로드
        // 🔧 FIX: field.exampleValue를 source of truth로 사용 (template.examples는 gap-fill)
        const mergedExamples = { ...(template.examples || {}) };
        singleFields.forEach(f => {
            if (typeof f === 'object' && f.key && f.exampleValue) {
                mergedExamples[f.key] = f.exampleValue;
            }
        });
        tableFields.forEach(t => {
            (t.columns || []).forEach(col => {
                if (typeof col === 'object' && col.key && (col.exampleValue || col.example)) {
                    mergedExamples[col.key] = col.exampleValue || col.example;
                }
            });
        });
        setExampleValues(mergedExamples);
        setCustomPrompt(template.customPrompt || '');
        setSystemPrompt(template.systemPrompt || ''); // 🆕 시스템 프롬프트 로드
        setEditMergeRules(template.mergeRules || []); // 📄 머지 규칙 로드
        setSelectedFields([]);
        setIsEditing(false);
        setSampleFileToSave(null); // Reset pending save file

        // 🎯 App.js에 템플릿 선택 알림 (샘플 탭 동기화용)
        if (onTemplateSelect) {
            onTemplateSelect(template.name);
        }

        // ℹ️ 샘플 이미지는 이제 App.js 샘플 탭에서 템플릿의 sampleImage URL을 직접 표시합니다.
        // onUpdateViewer를 호출하지 않아 fileList에 파일이 추가되지 않고, 샘플 탭이 유지됩니다.
    };

    const handleCreateNew = () => {
        setSelectedTemplateId(null);
        setEditName('새 템플릿');
        setEditDocType('custom');
        setEditFields([]); // 단일 필드 초기화
        setEditTableFields([]); // 🔧 테이블 필드 그룹 초기화
        setEditComputedFields([]); // 🔧 합성 필드 초기화
        setEditPrimaryKey('');
        setEditCategory("공통 (General)");
        setExampleValues({});
        setCustomPrompt('');
        setSystemPrompt(''); // 🆕 시스템 프롬프트 초기화
        setEditMergeRules([]); // 📄 머지 규칙 초기화
        setSelectedFields([]);
        setIsEditing(true);
    };



    const handleSave = async () => {
        if (!editName.trim()) {
            alert('템플릿 이름을 입력해주세요.');
            return;
        }
        // 🔧 수정: 단일 필드 또는 테이블 필드 중 하나 이상 필요
        if (editFields.length === 0 && editTableFields.length === 0) {
            alert('최소 1개 이상의 필드(단일 또는 테이블)를 선택해야 합니다.');
            return;
        }

        // ⭐ 저장 전 outputOrder 중복 검사 (자동 수정 X, 중복 시 저장 차단)
        const checkDuplicateOutputOrders = () => {
            // 모든 필드를 수집 (outputOrder > 0인 것만)
            const allFields = [];

            editFields.forEach((f, idx) => {
                if (f.outputOrder && f.outputOrder > 0) {
                    allFields.push({ order: f.outputOrder, name: f.displayName || f.key, type: '단일필드' });
                }
            });

            editTableFields.forEach((table, tIdx) => {
                table.columns?.forEach((col, cIdx) => {
                    if (col.outputOrder && col.outputOrder > 0) {
                        allFields.push({ order: col.outputOrder, name: col.displayName || col.key, type: '테이블' });
                    }
                });
            });

            editComputedFields.forEach((cf, idx) => {
                const order = cf.outputOrder ?? cf.order;
                if (order && order > 0) {
                    allFields.push({ order, name: cf.displayName || cf.key, type: '합성필드' });
                }
            });

            // 중복 찾기
            const orderCounts = {};
            allFields.forEach(f => {
                if (!orderCounts[f.order]) {
                    orderCounts[f.order] = [];
                }
                orderCounts[f.order].push(`${f.name} (${f.type})`);
            });

            // 중복된 순서만 필터링
            const duplicates = Object.entries(orderCounts)
                .filter(([order, fields]) => fields.length > 1)
                .map(([order, fields]) => `순서 ${order}: ${fields.join(', ')}`);

            return duplicates;
        };

        // 🔧 Auto-Resequence Output Orders before saving to prevent conflicts
        // This replaces the strict validation check
        let nextOrder = 1;

        // 1. Resequence Single & Computed Fields based on Unified Visual Order
        // We need to reconstruct the unified list logic here or iterate the state if unifiedFields is up to date
        // Since unifiedFields is a dependency of the component, it might changes during renders.
        // Safer to restart from current editFields/editComputedFields but sorted by their current outputOrder (or unifiedFields order if available)

        // Actually, unifiedFields is available in scope. Let's use it to determine the order of Single/Computed.
        // 🔧 FIX: Merge exampleValues into editFields before saving
        // The EXAMPLE column in the UI uses `exampleValues[field.key]` but editFields.exampleValue wasn't updated
        const mergedEditFields = editFields.map(field => ({
            ...field,
            exampleValue: exampleValues[field.key] !== undefined
                ? exampleValues[field.key]
                : field.exampleValue || ''
        }));

        const updatedSingleFields = [...mergedEditFields];
        const updatedComputedFields = [...editComputedFields];

        unifiedFields.forEach((field) => {
            if (field.type === 'single') {
                const fIndex = updatedSingleFields.findIndex(f => f.key === field.key);
                if (fIndex !== -1) updatedSingleFields[fIndex] = { ...updatedSingleFields[fIndex], outputOrder: nextOrder++ };
            } else if (field.type === 'computed') {
                const cIndex = updatedComputedFields.findIndex(f => f.key === field.key);
                if (cIndex !== -1) updatedComputedFields[cIndex] = { ...updatedComputedFields[cIndex], outputOrder: nextOrder++ };
            }
        });

        // 2. Resequence Table Columns (Flowing after Single/Computed)
        const updatedTableFields = editTableFields.map(table => ({
            ...table,
            columns: table.columns.map(col => ({
                ...col,
                outputOrder: nextOrder++
            }))
        }));

        try {
            setLoading(true);
            const id = selectedTemplateId || `tpl_${Date.now()}`;

            const templateData = {
                id,
                name: editName,
                documentType: editDocType,
                category: editCategory, // 카테고리 저장
                fields: updatedSingleFields, // 하위 호환: 기존 fields 유지 (단일 필드)
                singleFields: updatedSingleFields, // 🔧 새 구조: 단일 필드
                tableFields: updatedTableFields, // 🔧 새 구조: 테이블 필드 그룹
                computedFields: updatedComputedFields, // 🔧 합성 필드
                mergeRules: editMergeRules, // 📄 머지 규칙
                primaryKey: editPrimaryKey || '', // PK는 선택 사항 - 머지가 필요할 때만 설정
                examples: exampleValues,
                customPrompt: customPrompt.trim() || null,
                systemPrompt: systemPrompt.trim() || null // 🆕 시스템 프롬프트 저장
            };

            // 🚀 Smart Save: ALWAYS use FormData for consistency and robustness
            // This ensures the backend's fallback logic (reading req.body.id, req.body.name, etc.) works reliably.
            const formData = new FormData();

            // Append key fields individually (Critical for robust backend parsing)
            formData.append('id', templateData.id);
            formData.append('name', templateData.name);
            formData.append('documentType', templateData.documentType);

            // Category is optional but recommended
            if (templateData.category) {
                formData.append('category', templateData.category);
            } else {
                formData.append('category', "공통 (General)"); // Default
            }

            // Fields: Critical data (단일 필드 + 테이블 필드 + 합성 필드)
            formData.append('fields', JSON.stringify(templateData.fields));
            formData.append('tableFields', JSON.stringify(templateData.tableFields)); // 🔧 테이블 필드 추가
            formData.append('computedFields', JSON.stringify(templateData.computedFields)); // 🔧 합성 필드 추가
            formData.append('mergeRules', JSON.stringify(templateData.mergeRules)); // 📄 머지 규칙 추가

            // Append full template for completeness (backend tries this first, then falls back to above fields)
            formData.append('template', JSON.stringify(templateData));
            formData.append('files', JSON.stringify(templateData.files || [])); // Legacy support

            // 🎯 Debug: Check sampleFileToSave before sending
            console.log('🎯 [handleSave] sampleFileToSave:', {
                exists: !!sampleFileToSave,
                name: sampleFileToSave?.name,
                type: sampleFileToSave?.type,
                size: sampleFileToSave?.size
            });

            if (sampleFileToSave) {
                formData.append('sampleImage', sampleFileToSave);
                console.log('📸 sampleImage appended to FormData');
            } else {
                console.log('⚠️ No sampleFileToSave - sampleImage will not be sent');
            }

            const res = await fetch(`${apiBaseUrl}/api/templates`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: formData // Content-Type header is auto-set for FormData
            });

            if (res.ok) {
                const saved = await res.json();

                // [UX 개선] 리스트 즉시 업데이트 (낙관적 업데이트) 및 서버 동기화
                await fetchTemplates();

                // Optimistic update code (optional now, but good for flicker prevention - actually fetchTemplates covers it)
                // We keep fetchTemplates as the source of truth.

                // Find the updated template from the new list (or reuse saved.template)
                // selectTemplate might need to be re-run if we want to ensure latest data
                selectTemplate(saved.template);
                setSampleFileToSave(null); // Reset after save
                // 부모 컴포넌트에 알림
                if (onUpdate) onUpdate();

                // 🚀 UX Feedback for User
                alert('✅ 저장 완료!\n\n템플릿과 프롬프트가 성공적으로 업데이트되었습니다.');
            } else {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.error || '저장 실패');
            }
        } catch (err) {
            console.error('Error saving template:', err);
            // 🚀 Improved Error Alert
            alert(`저장 중 오류가 발생했습니다.\n${err.message || '알 수 없는 오류'}`);
        } finally {
            setLoading(false);
        }
    };

    // 📥 레거시 가져오기: D_OCR_HEADER_LAYOUT → D_OCR_HEADER_LAYOUT_EXT
    const handleImportLegacy = async (template, e) => {
        if (e) e.stopPropagation();
        if (!template.id?.startsWith('db_')) return;
        const presetId = parseInt(template.id.replace('db_', ''));
        if (!window.confirm(`"${template.name}" 템플릿을 가져오시겠습니까?\n(레거시 DB → EXT 테이블로 복사)`)) return;
        try {
            const res = await fetch(`${apiBaseUrl}/api/templates/import-legacy`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ presetId })
            });
            const data = await res.json();
            if (data.success) {
                alert(`✅ 가져오기 완료: ${template.name}\n새 ID: ${data.templateId}`);
                await fetchTemplates();
            } else {
                alert(`❌ 가져오기 실패: ${data.error}`);
            }
        } catch (err) {
            console.error('Import legacy error:', err);
            alert('❌ 가져오기 중 오류 발생');
        }
    };

    const handleDelete = async () => {
        if (!selectedTemplateId) return;
        if (!window.confirm('정말 삭제하시겠습니까?')) return;

        try {
            setLoading(true);
            const res = await fetch(`${apiBaseUrl}/api/templates/${selectedTemplateId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (res.ok) {
                await fetchTemplates();
                setSelectedTemplateId(null);
                setEditName('');
                setEditDocType('');
                setEditFields([]);

                // 부모 컴포넌트에 알림
                if (onUpdate) onUpdate();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // 📋 템플릿 복사
    const handleCopyTemplate = async () => {
        if (!selectedTemplateId) return;
        const sourceTemplate = templates.find(t => t.id === selectedTemplateId);
        if (!sourceTemplate) return;

        const copyName = window.prompt('복사할 템플릿 이름을 입력하세요:', `${sourceTemplate.name}_복사`);
        if (!copyName || !copyName.trim()) return;

        try {
            setLoading(true);
            const newId = `tpl_${Date.now()}`;
            const copied = {
                ...sourceTemplate,
                id: newId,
                name: copyName.trim(),
                sampleImage: undefined // 샘플 이미지는 복사하지 않음
            };

            const formData = new FormData();
            formData.append('id', copied.id);
            formData.append('name', copied.name);
            formData.append('documentType', copied.documentType || 'custom');
            formData.append('category', copied.category || '공통 (General)');
            formData.append('fields', JSON.stringify(copied.fields || []));
            formData.append('tableFields', JSON.stringify(copied.tableFields || []));
            formData.append('computedFields', JSON.stringify(copied.computedFields || []));
            formData.append('mergeRules', JSON.stringify(copied.mergeRules || []));
            formData.append('template', JSON.stringify(copied));

            const res = await fetch(`${apiBaseUrl}/api/templates`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: formData
            });

            if (res.ok) {
                const saved = await res.json();
                await fetchTemplates();
                selectTemplate(saved.template);
                if (onUpdate) onUpdate();
                alert(`"${copyName.trim()}" 템플릿이 생성되었습니다.`);
            } else {
                throw new Error('복사 실패');
            }
        } catch (err) {
            console.error('Template copy error:', err);
            alert('템플릿 복사 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 🚀 카테고리 이름 변경 (Batch Update)
    const handleRenameCategory = async (oldCategory, newCategory) => {
        if (!newCategory || !newCategory.trim()) return;
        if (oldCategory === newCategory) return;
        if (!process.env.REACT_APP_IS_DEMO && !window.confirm(`'${oldCategory}' 그룹에 속한 모든 템플릿을 '${newCategory}'(으)로 이동하시겠습니까?`)) return;

        // 해당 카테고리를 가진 템플릿 찾기
        const targetTemplates = templates.filter(t => (t.category || "공통 (General)") === oldCategory);

        setLoading(true);
        try {
            // 하나씩 순차 업데이트 (Batch API가 없으므로)
            for (const t of targetTemplates) {
                const updated = { ...t, category: newCategory };

                // 🚀 Use FormData for consistent, robust saving (mirrors handleSave logic)
                const formData = new FormData();
                formData.append('id', updated.id);
                formData.append('name', updated.name);
                formData.append('documentType', updated.documentType || 'custom');
                formData.append('category', newCategory); // Changed category
                formData.append('fields', JSON.stringify(updated.fields || []));

                // Append validation models/config if they exist
                if (updated.validationModels) formData.append('validationModels', JSON.stringify(updated.validationModels));
                if (updated.primaryKey) formData.append('primaryKey', updated.primaryKey);

                // Append full template for completeness (backend merges specific fields first)
                formData.append('template', JSON.stringify(updated));

                await fetch(`${apiBaseUrl}/api/templates`, {
                    method: 'POST',
                    body: formData // No Content-Type header needed for FormData
                });
            }
            // 완료 후 리로드
            await fetchTemplates();
            setTimeout(() => alert('카테고리 이름이 변경되었습니다.'), 100);
        } catch (error) {
            console.error(error);
            alert('카테고리 변경 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 2. 헤더 추출 (파일 업로드) -> 🚀 Auto-Generate Schema 통합
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 🎯 샘플 이미지로 저장할 파일 설정
        setSampleFileToSave(file);
        console.log('📸 샘플 파일 설정:', file.name);

        // ⭐ PDF 파일인 경우 페이지 선택 모달 표시
        const isPDF = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
        if (isPDF) {
            setPendingFile(file);
            setAutoGenSource('upload');
            await loadPdfThumbnails(file);
            setShowPageSelectionModal(true);
        } else {
            // 이미지 파일은 바로 Auto-Generate 실행
            setAutoGenSource('upload');
            await handleAutoGenerate(file);
            setAutoGenSource(null);
        }

        // 파일 인풋 초기화
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // 현재 파일에서 헤더 추출 핸들러 -> 🚀 Auto-Generate Schema 통합
    const handleExtractFromCurrent = async () => {
        if (!selectedFile) {
            alert('분석할 파일이 없습니다. 먼저 파일을 선택해주세요.');
            return;
        }

        // 🔧 Check if it's an actual File/Blob object
        const fileToAnalyze = selectedFile.file || selectedFile;
        if (!(fileToAnalyze instanceof File) && !(fileToAnalyze instanceof Blob)) {
            alert('현재 파일을 분석할 수 없습니다. 새 파일을 업로드해주세요.');
            console.error('selectedFile is not a File/Blob:', selectedFile);
            return;
        }

        // 🎯 샘플 이미지로 저장할 파일 설정
        setSampleFileToSave(fileToAnalyze);
        console.log('📸 샘플 파일 설정 (현재 파일):', fileToAnalyze.name || 'Blob');

        // ⭐ PDF 파일인 경우 페이지 선택 모달 표시
        const fileName = fileToAnalyze.name || selectedFile.name || '';
        const isPDF = fileToAnalyze.type === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
        if (isPDF) {
            setPendingFile(fileToAnalyze);
            setAutoGenSource('current');
            await loadPdfThumbnails(fileToAnalyze);
            setShowPageSelectionModal(true);
        } else {
            // 이미지 파일은 바로 Auto-Generate 실행
            setAutoGenSource('current');
            await handleAutoGenerate(fileToAnalyze);
            setAutoGenSource(null);
        }
    };

    // ⭐ PDF 페이지 썸네일 로드 함수
    const loadPdfThumbnails = async (file) => {
        setIsLoadingThumbnails(true);
        setPdfPageThumbnails([]);
        setSelectedPages([1]); // 기본값: 첫 페이지

        try {
            // PDF.js 라이브러리 동적 로드
            const pdfjsLib = await import('pdfjs-dist');
            pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            const numPages = pdf.numPages;
            setTotalPdfPages(numPages);

            console.log(`📄 PDF 로드 완료: ${numPages} 페이지`);

            // 각 페이지 썸네일 생성 (최대 20페이지)
            const maxThumbnails = Math.min(numPages, 20);
            const thumbnails = [];

            for (let i = 1; i <= maxThumbnails; i++) {
                try {
                    const page = await pdf.getPage(i);
                    const viewport = page.getViewport({ scale: 0.3 }); // 썸네일 크기

                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    await page.render({ canvasContext: context, viewport }).promise;
                    thumbnails.push({
                        pageNumber: i,
                        dataUrl: canvas.toDataURL('image/jpeg', 0.7)
                    });
                } catch (err) {
                    console.warn(`페이지 ${i} 썸네일 생성 실패:`, err);
                    thumbnails.push({ pageNumber: i, dataUrl: null });
                }
            }

            setPdfPageThumbnails(thumbnails);
            console.log(`✅ ${thumbnails.length}개 썸네일 생성 완료`);
        } catch (error) {
            console.error('PDF 썸네일 로드 실패:', error);
            alert('PDF 파일을 읽는 중 오류가 발생했습니다.');
            setShowPageSelectionModal(false);
        } finally {
            setIsLoadingThumbnails(false);
        }
    };

    // ⭐ 페이지 선택 토글
    const togglePageSelection = (pageNum) => {
        setSelectedPages(prev => {
            if (prev.includes(pageNum)) {
                // 최소 1개는 선택되어야 함
                if (prev.length > 1) {
                    return prev.filter(p => p !== pageNum);
                }
                return prev;
            }
            return [...prev, pageNum].sort((a, b) => a - b);
        });
    };

    // ⭐ 페이지 범위 선택 (시작~종료)
    const selectPageRange = (start, end) => {
        const pages = [];
        for (let i = start; i <= end && i <= totalPdfPages; i++) {
            pages.push(i);
        }
        setSelectedPages(pages);
    };

    // ⭐ 페이지 선택 확인 및 분석 시작
    const handlePageSelectionConfirm = async () => {
        if (!pendingFile || selectedPages.length === 0) {
            alert('분석할 페이지를 선택해주세요.');
            return;
        }

        console.log(`📄 선택된 페이지로 분석 시작: ${selectedPages.join(', ')}`);
        setShowPageSelectionModal(false);

        // handleAutoGenerate에 선택된 페이지 정보 전달
        await handleAutoGenerate(pendingFile, selectedPages);

        setAutoGenSource(null);
        setPendingFile(null);
        setSelectedPages([1]);
        setPdfPageThumbnails([]);
    };

    // ⭐ 페이지 선택 모달 취소
    const handlePageSelectionCancel = () => {
        setShowPageSelectionModal(false);
        setAutoGenSource(null);
        setPendingFile(null);
        setSelectedPages([1]);
        setPdfPageThumbnails([]);
    };

    const toggleFieldSelection = (fieldKey, event, visualIndex) => {
        console.log('toggleFieldSelection', { fieldKey, shift: event?.shiftKey, last: lastSelectedFieldKey, vIdx: visualIndex });

        // Shift Key Handling
        if (event && event.shiftKey && lastSelectedFieldKey !== null) {
            const lastIndex = unifiedFields.findIndex(f => f.key === lastSelectedFieldKey);
            const currentIndex = visualIndex ?? unifiedFields.findIndex(f => f.key === fieldKey);

            console.log('Shift-Click Range:', { lastIndex, currentIndex });

            if (lastIndex !== -1 && currentIndex !== -1) {
                const start = Math.min(lastIndex, currentIndex);
                const end = Math.max(lastIndex, currentIndex);
                const rangeKeys = unifiedFields.slice(start, end + 1)
                    .filter(f => f.type === 'single') // Only select single fields for now as they share the selection state
                    .map(f => f.key);

                console.log('Range Keys to Select:', rangeKeys);

                setSelectedFields(prev => {
                    const newSet = new Set(prev);
                    rangeKeys.forEach(key => newSet.add(key));
                    return Array.from(newSet);
                });
                return; // Early return to skip default toggle
            }
        }

        // Default Toggle Behavior
        setLastSelectedFieldKey(fieldKey);
        setSelectedFields(prev => {
            if (prev.includes(fieldKey)) {
                return prev.filter(f => f !== fieldKey);
            } else {
                return [...prev, fieldKey];
            }
        });
    };

    const toggleAllSelection = () => {
        if (selectedFields.length === editFields.length) {
            setSelectedFields([]);
        } else {
            setSelectedFields(editFields.map(f => f.key));
        }
    };

    const removeSelectedFields = () => {
        if (selectedFields.length === 0) return;
        if (!window.confirm(`선택한 ${selectedFields.length}개 필드를 삭제하시겠습니까?`)) return;

        setEditFields(prev => prev.filter(f => !selectedFields.includes(f.key)));
        if (selectedFields.includes(editPrimaryKey)) setEditPrimaryKey('');
        setSelectedFields([]);
        setIsEditing(true);
    };

    // 🔧 테이블 컬럼 선택 토글
    const toggleTableColumnSelection = (tableIdx, colIdx) => {
        const colId = `${tableIdx}-${colIdx}`;
        setSelectedTableColumns(prev =>
            prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]
        );
    };

    // 🔧 테이블 컬럼 선택 삭제
    const removeSelectedTableColumns = () => {
        if (selectedTableColumns.length === 0) return;
        if (!window.confirm(`선택한 ${selectedTableColumns.length}개 테이블 컬럼을 삭제하시겠습니까?`)) return;

        setEditTableFields(prev => {
            const newTables = prev.map(table => ({
                ...table,
                columns: [...table.columns]
            }));

            // ⭐ 인덱스를 내림차순으로 정렬하여 삭제 (인덱스 변동 방지)
            const sortedColIds = [...selectedTableColumns].sort((a, b) => {
                const [tIdxA, cIdxA] = a.split('-').map(Number);
                const [tIdxB, cIdxB] = b.split('-').map(Number);
                if (tIdxA === tIdxB) return cIdxB - cIdxA;
                return tIdxB - tIdxA;
            });

            sortedColIds.forEach(colId => {
                const [tIdx, cIdx] = colId.split('-').map(Number);
                if (newTables[tIdx] && newTables[tIdx].columns[cIdx]) {
                    newTables[tIdx].columns.splice(cIdx, 1);
                }
            });

            // 빈 테이블 제거 옵션
            return newTables.filter(t => t.columns.length > 0 || window.confirm(`'${t.tableName}' 테이블의 모든 컬럼이 삭제되었습니다. 테이블도 삭제할까요?`) === false);
        });
        setSelectedTableColumns([]);
        setIsEditing(true);
    };

    const removeField = (fieldKey) => {
        setEditFields(prev => prev.filter(f => f.key !== fieldKey));
        // 🔧 Also remove the example value for this field
        setExampleValues(prev => {
            const updated = { ...prev };
            delete updated[fieldKey];
            return updated;
        });
        if (editPrimaryKey === fieldKey) setEditPrimaryKey('');
        setIsEditing(true);
    };

    const getNextOutputOrder = () => {
        const maxSingle = Math.max(0, ...editFields.map(f => f.outputOrder || 0));
        const maxComputed = Math.max(0, ...editComputedFields.map(f => (f.outputOrder ?? f.order) || 0));
        const maxTable = Math.max(0, ...editTableFields.flatMap(t => t.columns.map(c => c.outputOrder || 0)));
        return Math.max(maxSingle, maxComputed, maxTable) + 1;
    };

    const addCustomField = () => {
        // 🚀 바로 빈 행 추가 (prompt 대화상자 없이)
        const newFieldNum = editFields.length + 1;
        const nextOrder = getNextOutputOrder();
        const fieldKey = `Field_${newFieldNum}`;
        const newField = {
            key: fieldKey,
            type: 'string',
            description: getDefaultDescription(fieldKey),
            displayName: '',
            outputOrder: nextOrder
        };
        setEditFields(prev => [...prev, newField]);
        setIsEditing(true);
    };

    // ⭐ 자동 재정렬 함수: 새 순서 입력 시 충돌하는 필드들을 밀어줌
    const autoReorderFields = (newOrder, excludeFieldId, excludeFieldType) => {
        // DnD 도입으로 인해 수동 입력 시 자동 재정렬 로직은 단순화하거나 DnD와 충돌하지 않도록 조정 필요
        // 일단 유지하되 로그만 남김
        console.log(`🔄 [AutoReorder] Triggered (Legacy)`);
    };

    // 🚀 DnD Sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // 🚀 Unified Fields for DnD (Single + Computed)
    const unifiedFields = useMemo(() => {
        // Use index-based ID to prevent duplicates if user enters same key
        const single = editFields.map((f, i) => ({ ...f, id: `single-${i}`, originalIndex: i, type: 'single', sortOrder: f.outputOrder || (i + 1) }));
        const computed = editComputedFields.map((f, i) => {
            // 순서 보정: 합성 필드의 순서가 없으면 맨 뒤로
            const fallbackOrder = editFields.length + editTableFields.reduce((sum, t) => sum + (t.columns?.length || 0), 0) + i + 1;
            return { ...f, id: `computed-${i}`, originalIndex: i, type: 'computed', sortOrder: (f.outputOrder ?? f.order) || fallbackOrder };
        });

        // Sort by outputOrder to display in correct visual order
        return [...single, ...computed].sort((a, b) => a.sortOrder - b.sortOrder);
    }, [editFields, editComputedFields, editTableFields]);

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = unifiedFields.findIndex(item => item.id === active.id);
            const newIndex = unifiedFields.findIndex(item => item.id === over.id);

            // 1. Array Move for Unified List
            const newUnifiedOrder = arrayMove(unifiedFields, oldIndex, newIndex);

            // 2. Update outputOrder based on new visual index (1-based)
            //    AND separate back into editFields and editComputedFields
            const newSingleFields = [...editFields];
            const newComputedFields = [...editComputedFields];

            newUnifiedOrder.forEach((item, visualIndex) => {
                const newOrder = visualIndex + 1; // 1-based order

                if (item.type === 'single') {
                    // Update Single Field
                    newSingleFields[item.originalIndex] = {
                        ...newSingleFields[item.originalIndex],
                        outputOrder: newOrder
                    };
                } else if (item.type === 'computed') {
                    // Update Computed Field
                    newComputedFields[item.originalIndex] = {
                        ...newComputedFields[item.originalIndex],
                        outputOrder: newOrder,
                        order: newOrder // Sync legacy order field
                    };
                }
            });

            // 3. Update State
            // Note: We don't reorder the ARRAYS themselves (`editFields`), we only update their `outputOrder` property.
            // The `unifiedFields` memo will then re-sort them correctly based on `outputOrder`.
            // However, to keep "No." column consistent with array index for standard behavior, 
            // some might expect `editFields` array to actually change order. 
            // BUT normalized data usually relies on ID/Key. The `outputOrder` is the display property.
            // We will stick to updating properties.

            // 4. Update Table Columns to start AFTER the last Single/Computed field to avoid overlap
            const startOrderForTables = newUnifiedOrder.length + 1;
            const newTableFields = editTableFields.map(table => ({
                ...table,
                columns: table.columns.map((col, cIdx) => ({
                    ...col,
                    // If table columns have their own relative order, we preserve it but shift the base?
                    // Or we just leave them alone? 
                    // Let's shift them to ensure global uniqueness if possible, but minimal impact.
                    // For now, let's JUST update Single/Computed. 
                    // If user manually mixed table columns, this auto-renumber might overwrite?
                    // Yes, so we should arguably re-base table columns too.
                    // outputOrder: startOrderForTables + (currentRelativeOffset)
                }))
            }));
            // Skipped auto-updating tables for now to reduce complexity/risk. User can manually adjust if needed.

            setEditFields(newSingleFields);
            setEditComputedFields(newComputedFields);
            setIsEditing(true);
        }
    };


    // 필드 속성 변경 핸들러 (Key, Type, Description)
    const handleFieldChange = (index, property, value) => {
        const newFields = [...editFields];
        const oldKey = newFields[index].key;

        newFields[index] = { ...newFields[index], [property]: value };
        setEditFields(newFields);

        // ⭐ outputOrder 변경 - 저장 시점에 충돌 해결 (입력 시점 재정렬은 비동기 상태 문제로 제거)
        // 사용자가 저장 버튼을 클릭하면 resolveOutputOrderConflicts()가 중복을 해결함

        // Key 변경 시 관련 데이터 업데이트 (PK, ExampleValue, Selection)
        if (property === 'key') {
            const newKey = value;
            if (editPrimaryKey === oldKey) setEditPrimaryKey(newKey);

            if (exampleValues[oldKey]) {
                setExampleValues(prev => {
                    const next = { ...prev };
                    next[newKey] = next[oldKey];
                    delete next[oldKey];
                    return next;
                });
            }

            if (selectedFields.includes(oldKey)) {
                setSelectedFields(prev => prev.map(f => f === oldKey ? newKey : f));
            }

            // 🏷️ Key 변경 시 description이 비어있으면 DTO 기본값 자동 채우기
            if (!newFields[index].description) {
                const defaultDesc = getDefaultDescription(newKey);
                if (defaultDesc) {
                    newFields[index] = { ...newFields[index], description: defaultDesc };
                    setEditFields([...newFields]);
                }
            }
        }
        setIsEditing(true);
    };

    // 필드 순서 변경 함수
    const moveField = (index, direction) => {
        const newFields = [...editFields];
        if (direction === 'up' && index > 0) {
            [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
        } else if (direction === 'down' && index < newFields.length - 1) {
            [newFields[index + 1], newFields[index]] = [newFields[index], newFields[index + 1]];
        }
        setEditFields(newFields);
        setIsEditing(true);
    };

    // 🔧 테이블 필드 그룹 관리 함수들
    const addTableGroup = () => {
        const tableNum = editTableFields.length + 1;
        setEditTableFields(prev => [...prev, {
            tableName: `Table_${tableNum}`,
            description: '',
            columns: [
                { key: 'no', type: 'string', description: '번호' }
            ]
        }]);
        setIsEditing(true);
    };

    const removeTableGroup = (tableIndex) => {
        if (window.confirm('이 테이블 그룹을 삭제하시겠습니까?')) {
            setEditTableFields(prev => prev.filter((_, idx) => idx !== tableIndex));
            setIsEditing(true);
        }
    };

    const addTableColumn = (tableIndex) => {
        setEditTableFields(prev => {
            const newTables = [...prev];
            const colNum = newTables[tableIndex].columns.length + 1;
            newTables[tableIndex] = {
                ...newTables[tableIndex],
                columns: [...newTables[tableIndex].columns, {
                    key: `Column_${colNum}`,
                    type: 'string',
                    description: '',
                    displayName: '',
                    outputOrder: editFields.length + tableIndex * 100 + colNum
                }]
            };
            return newTables;
        });
        setIsEditing(true);
    };

    const removeTableColumn = (tableIndex, columnIndex) => {
        setEditTableFields(prev => {
            const newTables = [...prev];
            newTables[tableIndex] = {
                ...newTables[tableIndex],
                columns: newTables[tableIndex].columns.filter((_, idx) => idx !== columnIndex)
            };
            return newTables;
        });
        setIsEditing(true);
    };

    // 테이블 컬럼 순서 변경
    const moveTableColumn = (tableIndex, columnIndex, direction) => {
        setEditTableFields(prev => {
            const newTables = [...prev];
            const columns = [...newTables[tableIndex].columns];

            const swap = (idx1, idx2) => {
                // Swap outputOrder logic for WYSIWYG correctness
                const order1 = columns[idx1].outputOrder;
                const order2 = columns[idx2].outputOrder;

                // Only swap if both are valid numbers
                if (typeof order1 === 'number' && typeof order2 === 'number') {
                    columns[idx1] = { ...columns[idx1], outputOrder: order2 };
                    columns[idx2] = { ...columns[idx2], outputOrder: order1 };
                }

                // Swap array position
                [columns[idx1], columns[idx2]] = [columns[idx2], columns[idx1]];
            };

            if (direction === 'up' && columnIndex > 0) {
                swap(columnIndex - 1, columnIndex);
            } else if (direction === 'down' && columnIndex < columns.length - 1) {
                swap(columnIndex, columnIndex + 1);
            }
            newTables[tableIndex] = { ...newTables[tableIndex], columns };
            return newTables;
        });
        setIsEditing(true);
    };

    const updateTableColumn = (tableIndex, columnIndex, property, value) => {
        setEditTableFields(prev => {
            const newTables = [...prev];
            const newColumns = [...newTables[tableIndex].columns];
            newColumns[columnIndex] = { ...newColumns[columnIndex], [property]: value };
            newTables[tableIndex] = { ...newTables[tableIndex], columns: newColumns };
            return newTables;
        });

        // ⭐ outputOrder 변경 - 저장 시점에 충돌 해결

        setIsEditing(true);
    };

    const updateTableGroupName = (tableIndex, property, value) => {
        setEditTableFields(prev => {
            const newTables = [...prev];
            newTables[tableIndex] = { ...newTables[tableIndex], [property]: value };
            return newTables;
        });
        setIsEditing(true);
    };

    // 🔧 합성 필드 관리 함수들
    const getAvailableSourceFields = () => {
        // 단일 필드
        const single = editFields.map(f => ({
            key: f.key,
            displayName: f.displayName || f.key,
            source: 'single'
        }));
        // 테이블 필드
        const table = editTableFields.flatMap(t =>
            t.columns.map(c => ({
                key: `${t.tableName}.${c.key}`,
                displayName: `[${t.tableName}] ${c.displayName || c.key}`,
                source: 'table',
                tableName: t.tableName,
                columnKey: c.key
            }))
        );
        return [...single, ...table];
    };

    const addComputedField = () => {
        const num = editComputedFields.length + 1;
        const nextOrder = getNextOutputOrder();

        setEditComputedFields(prev => [...prev, {
            key: `computed_${num}`,
            displayName: `합성필드${num}`,
            formula: '',
            sourceFields: [],
            order: nextOrder,
            outputOrder: nextOrder
        }]);
        setIsEditing(true);
    };

    const removeComputedField = (index) => {
        setEditComputedFields(prev => prev.filter((_, idx) => idx !== index));
        setIsEditing(true);
    };

    const updateComputedField = (index, property, value) => {
        setEditComputedFields(prev => {
            const newFields = [...prev];
            newFields[index] = { ...newFields[index], [property]: value };
            return newFields;
        });

        // ⭐ outputOrder 변경 - 저장 시점에 충돌 해결

        setIsEditing(true);
    };

    // 포뮬러에서 소스 필드 자동 추출
    const parseFormulaSourceFields = (formula) => {
        const matches = formula.match(/\{([^}]+)\}/g) || [];
        return matches.map(m => m.slice(1, -1));
    };

    // 렌더링: 모달 vs 사이드 드로어
    const containerClasses = isModal
        ? "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        : "h-full flex flex-col bg-white border-l border-gray-200 shadow-xl";

    const innerClasses = isModal
        ? "bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
        : "flex-1 flex flex-col overflow-hidden";

    // 테이블 헤더 스타일
    const thClass = "px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 bg-gray-50 sticky top-0";
    // 테이블 셀 스타일
    const tdClass = "px-2 py-1 text-xs text-gray-700 border-b border-gray-100";

    // 프롬프트 미리보기 로드 (🔧 Fixed: useCallback to prevent stale closure)
    const loadPreviewPrompt = useCallback(async () => {
        if (isGenerating) return; // 이미 생성 중이면 중복 요청 방지

        try {
            setIsGenerating(true);
            setPreviewPrompt(''); // 로딩 중 비우기 (또는 스켈레톤 UI)
            console.log('🔄 프롬프트 미리보기 로드 시작...');
            console.log('📊 Current editFields count:', editFields.length);
            const payload = {
                name: editName,
                fields: editFields,
                tableFields: editTableFields, // 🔧 테이블 필드 추가
                examples: exampleValues
            };
            console.log('📤 Payload fields:', payload.fields.map(f => f.key || f));
            console.log('📤 Payload tableFields:', payload.tableFields?.map(t => t.tableName));

            const res = await fetch(`${apiBaseUrl}/api/templates/preview-prompt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                console.error('❌ API Error:', res.status, res.statusText);
                throw new Error(`API Error: ${res.status}`);
            }

            const data = await res.json();
            console.log('📥 Received Data:', data);

            if (data.prompt) {
                setPreviewPrompt(data.prompt);
                console.log('✅ previewPrompt updated');
            } else {
                console.warn('⚠️ No prompt in response data');
                setPreviewPrompt('(프롬프트 생성 실패: 서버 응답에 내용이 없습니다.)');
            }
        } catch (error) {
            console.error('❌ 프롬프트 미리보기 실패:', error);
            setPreviewPrompt(`(오류 발생: ${error.message})`);
        } finally {
            setIsGenerating(false);
        }
    }, [editFields, editTableFields, editName, exampleValues, apiBaseUrl]); // 🔧 Added editTableFields

    // 🚀 [Auto-Schema] State & Logic
    const [isAutoSchemaModalOpen, setIsAutoSchemaModalOpen] = useState(false);
    const [autoSchemaInstructions, setAutoSchemaInstructions] = useState('');
    const [isAutoGenerating, setIsAutoGenerating] = useState(false);
    const [autoGenProgress, setAutoGenProgress] = useState(0); // 0-100%
    // autoGenSource は上部で定義済み
    const autoSchemaFileRef = useRef(null);

    const handleAutoGenerate = async (file, selectedPagesParam = null) => {
        if (!file) {
            alert('분석할 파일이 없습니다.');
            return;
        }

        // 🔧 Validation: Duck typing으로 파일 검증 (instanceof 대신)
        const isValidFile = file && typeof file === 'object' &&
            (file.name || file.size) &&
            (typeof file.arrayBuffer === 'function' || typeof file.slice === 'function');

        if (!isValidFile) {
            console.error('⚠️ handleAutoGenerate received invalid file object:', file, typeof file);
            alert('파일 객체가 유효하지 않습니다. 새 파일을 업로드해주세요.');
            return;
        }

        console.log('📤 [handleAutoGenerate] Starting with file:', file.name, 'size:', file.size);
        if (selectedPagesParam) {
            console.log('📄 [handleAutoGenerate] Selected pages:', selectedPagesParam.join(', '));
        }

        setIsAutoGenerating(true);
        setAutoGenProgress(0);

        // Progress Simulation
        const progressInterval = setInterval(() => {
            setAutoGenProgress(prev => {
                if (prev >= 95) return 95; // Cap at 95%
                // Slow down as it gets higher
                const increment = prev < 50 ? 10 : prev < 80 ? 5 : 1;
                return prev + increment;
            });
        }, 800); // Update every 800ms

        try {
            try {
                setIsAutoGenerating(true);
                const formData = new FormData();
                formData.append('image', file);
                formData.append('instructions', autoSchemaInstructions);
                formData.append('language', schemaLanguage);  // 한글/영문 선택

                // ⭐ 선택된 페이지 정보 전달 (다중 페이지 지원)
                if (selectedPagesParam && selectedPagesParam.length > 0) {
                    formData.append('selectedPages', JSON.stringify(selectedPagesParam));
                }

                // 🚀 Sync Viewer & Set for Saving
                setSampleFileToSave(file);
                if (onUpdateViewer) onUpdateViewer(file);

                // 🚀 Safeguard: New vs Edit Mode
                if (selectedTemplateId) {
                    const userChoice = window.confirm(
                        `[편집 모드 경고] 현재 '${editName || '템플릿'}'을 수정 중입니다.\n\n` +
                        `'확인'을 누르면 현재 템플릿의 필드가 덮어씌워집니다.\n` +
                        `'취소'를 누르면 [새 템플릿]으로 모드를 전환하여 생성합니다.`
                    );

                    if (!userChoice) {
                        // Switch to New Mode
                        handleCreateNew();
                        // Wait a tick for state update (though handleCreateNew is sync usually)
                        // But we proceed to generate schema for the NEW template
                        console.log('User switched to New Template mode.');
                    } else {
                        console.log('User confirmed overwrite of existing template.');
                    }
                }

                const res = await fetch(`${apiBaseUrl}/api/admin/generate-schema`, {
                    method: 'POST',
                    body: formData
                });

                const data = await res.json();
                if (data.success && data.schema) {
                    // 🚀 Normalize and separate single fields vs table fields
                    const allFields = normalizeFields(data.schema);

                    // 🔧 단일 필드와 테이블 필드 분리
                    const singleFields = [];
                    const tableFieldsFromSchema = [];

                    allFields.forEach(field => {
                        if (field.type === 'array' && field.items) {
                            // 테이블 필드로 변환
                            // 🔧 Fix: Add outputOrder to table columns
                            const baseOrder = singleFields.length + (tableFieldsFromSchema.length * 100);
                            const tableField = {
                                tableName: field.key,
                                description: field.description || '',
                                columns: field.items.map((item, colIdx) => ({
                                    key: item.key,
                                    type: item.type || 'string',
                                    description: item.description || getDefaultDescription(item.key),
                                    example: item.exampleValue || '',
                                }))
                            };
                            tableFieldsFromSchema.push(tableField);
                        } else {
                            // 단일 필드 — description이 비어있으면 DTO 기본값 자동 채우기
                            if (!field.description) {
                                field.description = getDefaultDescription(field.key);
                            }
                            singleFields.push(field);
                        }
                    });

                    setEditFields(singleFields);
                    setEditTableFields(tableFieldsFromSchema);

                    // 🚀 Populate Example Values from Auto-Schema
                    const newExamples = {};
                    singleFields.forEach(f => {
                        if (f.exampleValue) newExamples[f.key] = f.exampleValue;
                    });
                    setExampleValues(newExamples);
                    setIsEditing(true);
                    setIsAutoSchemaModalOpen(false);
                    setAutoSchemaInstructions('');

                    const tableCount = tableFieldsFromSchema.length;
                    const tableColCount = tableFieldsFromSchema.reduce((sum, t) => sum + t.columns.length, 0);
                    alert(`✨ 자동 분석 완료!\n\n• 단일 필드: ${singleFields.length}개\n• 테이블: ${tableCount}개 (컬럼 ${tableColCount}개)`);
                } else {
                    throw new Error(data.error || 'Failed to generate schema');
                }
            } catch (error) {
                console.error('Auto-Schema Error:', error);
                alert('스키마 생성 실패: ' + error.message); // 에러 피드백 활성화
            } finally {
                setIsAutoGenerating(false);
            }
            setAutoGenProgress(100); // Complete
            console.log('✅ Auto-Schema 적용 완료');
            // toast.success('자동 분석 완료!'); // Assuming toast is available
        } catch (err) {
            console.error('❌ Auto-Schema Error:', err);
            // toast.error('자동 분석 실패: ' + err.message); // Assuming toast is available
        } finally {
            clearInterval(progressInterval);
            setTimeout(() => {
                setIsAutoGenerating(false);
                setAutoGenProgress(0);
            }, 500); // Brief delay to show 100%
        }
    };

    // Sub-component for Auto-Schema Instructions
    const AutoSchemaModal = () => {
        const [localFile, setLocalFile] = useState(null);

        const onFileChange = (e) => {
            if (e.target.files?.[0]) setLocalFile(e.target.files[0]);
        };

        const onRun = () => {
            // Priority: Uploaded File > Selected File in List
            const targetFile = localFile || selectedFile;
            if (!targetFile) {
                alert('Please upload a document to analyze.');
                return;
            }
            handleAutoGenerate(targetFile);
        };

        return (
            <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col animate-fadeIn">
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4 flex justify-between items-center">
                        <div className="text-white">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Sparkles className="w-5 h-5" /> Auto-Generate Schema
                            </h3>
                            <p className="text-xs text-gray-200 opacity-90">AI will analyze your document structure.</p>
                        </div>
                        <button onClick={() => setIsAutoSchemaModalOpen(false)} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="p-6 flex-1 text-sm text-gray-700 space-y-4">
                        <div className="space-y-2">
                            <label className="font-bold block text-gray-800">1. Sample Document</label>
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={onFileChange}
                                className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-gray-900 hover:file:bg-gray-200 transition-all border border-gray-200 rounded-lg p-2"
                            />
                            {!localFile && selectedFile && (
                                <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                    <Check className="w-3 h-3" /> Using currently selected file: {selectedFile.name}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="font-bold block text-gray-800">2. Instructions (Optional)</label>
                            <textarea
                                value={autoSchemaInstructions}
                                onChange={(e) => setAutoSchemaInstructions(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-gray-600 focus:border-transparent transition-all"
                                rows={3}
                                placeholder="e.g., Extract 'Shipper Name' and 'Total Amount'. For the table, extracting 'Description', 'Qty', and 'Unit Price' is enough."
                            />
                        </div>

                        <div className="bg-gray-100 p-3 rounded-lg border border-gray-200 text-xs text-gray-900 leading-relaxed">
                            <p className="font-bold mb-1">💡 Tip:</p>
                            AI will automatically detect key fields and tables. Detailed instructions help improve accuracy for specific templates.
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
                        <button
                            onClick={() => setIsAutoSchemaModalOpen(false)}
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors font-medium shadow-sm"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onRun}
                            disabled={isAutoGenerating}
                            className={`px-5 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-gray-900 shadow-md font-bold transition-all flex items-center gap-2 ${isAutoGenerating ? 'opacity-70 cursor-wait' : ''}`}
                        >
                            {isAutoGenerating ? 'Analyzing...' : <><Sparkles className="w-4 h-4" /> Generate Schema</>}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ⭐ PDF 페이지 선택 모달 컴포넌트
    const PageSelectionModal = () => {
        const [startPage, setStartPage] = useState(1);
        const [endPage, setEndPage] = useState(1);

        const handleRangeApply = () => {
            const start = Math.max(1, Math.min(startPage, totalPdfPages));
            const end = Math.max(start, Math.min(endPage, totalPdfPages));
            selectPageRange(start, end);
        };

        const handleSelectAll = () => {
            const allPages = [];
            for (let i = 1; i <= totalPdfPages; i++) {
                allPages.push(i);
            }
            setSelectedPages(allPages);
        };

        return (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    {/* 헤더 */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-100 to-white">
                        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            📄 헤더 추출 페이지 설정
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            전체 {totalPdfPages}페이지 중 <span className="font-bold text-gray-800">{selectedPages.length}페이지</span> 선택됨
                            <span className="text-xs text-gray-400 ml-2">(여러 페이지 선택 시 필드가 병합됩니다)</span>
                        </p>
                    </div>

                    {/* 범위 선택 컨트롤 */}
                    <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">시작:</span>
                            <input
                                type="number"
                                value={startPage}
                                onChange={(e) => setStartPage(parseInt(e.target.value) || 1)}
                                min={1}
                                max={totalPdfPages}
                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-gray-600"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">종료:</span>
                            <input
                                type="number"
                                value={endPage}
                                onChange={(e) => setEndPage(parseInt(e.target.value) || 1)}
                                min={1}
                                max={totalPdfPages}
                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-gray-600"
                            />
                        </div>
                        <button
                            onClick={handleRangeApply}
                            className="px-3 py-1 text-xs bg-gray-200 text-gray-900 rounded hover:bg-gray-300 transition-colors"
                        >
                            범위 적용
                        </button>
                        <button
                            onClick={() => setSelectedPages([1])}
                            className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                        >
                            첫 페이지만
                        </button>
                        <button
                            onClick={handleSelectAll}
                            className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                        >
                            전체 선택
                        </button>
                    </div>

                    {/* 썸네일 그리드 */}
                    <div className="flex-1 overflow-y-auto p-4">
                        {isLoadingThumbnails ? (
                            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-800 mb-3"></div>
                                <p>PDF 페이지 로딩 중...</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                                {pdfPageThumbnails.map((thumb) => (
                                    <div
                                        key={thumb.pageNumber}
                                        onClick={() => togglePageSelection(thumb.pageNumber)}
                                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:shadow-md ${selectedPages.includes(thumb.pageNumber)
                                            ? 'border-gray-600 ring-2 ring-gray-300 bg-gray-100'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        {thumb.dataUrl ? (
                                            <img
                                                src={thumb.dataUrl}
                                                alt={`Page ${thumb.pageNumber}`}
                                                className="w-full h-auto"
                                            />
                                        ) : (
                                            <div className="w-full h-24 bg-gray-100 flex items-center justify-center text-gray-400">
                                                ?
                                            </div>
                                        )}
                                        <div className={`absolute bottom-0 left-0 right-0 text-center py-1 text-xs font-bold ${selectedPages.includes(thumb.pageNumber)
                                            ? 'bg-gray-600 text-white'
                                            : 'bg-gray-800/60 text-white'
                                            }`}>
                                            {thumb.pageNumber}
                                        </div>
                                        {selectedPages.includes(thumb.pageNumber) && (
                                            <div className="absolute top-1 right-1 w-5 h-5 bg-gray-600 rounded-full flex items-center justify-center shadow">
                                                <Check className="w-3 h-3 text-white" />
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {/* 20페이지 이상일 때 안내 */}
                                {totalPdfPages > 20 && (
                                    <div className="col-span-full text-center text-xs text-gray-400 mt-2">
                                        ℹ️ 썸네일은 처음 20페이지만 표시됩니다. 범위 선택으로 다른 페이지를 지정하세요.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 푸터 */}
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                            선택된 페이지: <span className="font-mono text-gray-800">{selectedPages.join(', ')}</span>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handlePageSelectionCancel}
                                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors font-medium"
                            >
                                취소
                            </button>
                            <button
                                onClick={handlePageSelectionConfirm}
                                disabled={selectedPages.length === 0}
                                className="px-5 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-gray-900 shadow-md font-bold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Sparkles className="w-4 h-4" />
                                {selectedPages.length}개 페이지 분석
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // AI 프롬프트 리파인 핸들러
    const handleRefinePrompt = async () => {
        if (!refinementInstructions.trim()) {
            alert('수정 요청 사항을 입력해주세요.');
            return;
        }

        try {
            setIsRefining(true);
            const payload = {
                currentPrompt: customPrompt || previewPrompt, // 현재 프롬프트 (커스텀이 없으면 자동 생성된 것 기준)
                fields: editFields,
                examples: exampleValues,
                instructions: refinementInstructions
            };

            const res = await fetch(`${apiBaseUrl}/api/templates/refine-prompt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (data.success) {
                setCustomPrompt(data.refinedPrompt); // 결과로 커스텀 프롬프트 덮어쓰기
                setIsEditing(true);
                alert('프롬프트가 수정되었습니다. "사용자 정의 프롬프트" 내용을 확인하세요.');
            } else {
                throw new Error(data.error);
            }
        } catch (error) {
            console.error('프롬프트 수정 실패:', error);
            alert('프롬프트 수정 중 오류가 발생했습니다: ' + error.message);
        } finally {
            setIsRefining(false);
        }
    };

    // 🆕 AI 가이드: 시스템 프롬프트를 구조화된 형식으로 변환
    const handleEnhanceSystemPrompt = async () => {
        if (!systemPrompt.trim()) {
            alert('먼저 시스템 프롬프트를 입력해주세요.\n예: "규격1,2,3 순서 유지, 페이지 넘어가도 이어짐"');
            return;
        }

        try {
            setIsEnhancingSystemPrompt(true);

            const res = await fetch(`${apiBaseUrl}/api/templates/enhance-system-prompt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userInput: systemPrompt,
                    templateName: editName,
                    fields: editFields.map(f => f.key || f),
                    tableFields: editTableFields.flatMap(t => (t.columns || []).map(c => c.key || c))
                })
            });

            const data = await res.json();
            if (data.success && data.enhancedPrompt) {
                setSystemPrompt(data.enhancedPrompt);
                setIsEditing(true);
                setToast({ show: true, message: '✨ AI가 프롬프트를 개선했습니다!' });
            } else {
                throw new Error(data.error || 'AI 응답 실패');
            }
        } catch (error) {
            console.error('AI 가이드 실패:', error);
            alert('AI 가이드 중 오류가 발생했습니다: ' + error.message);
        } finally {
            setIsEnhancingSystemPrompt(false);
        }
    };

    // 🚀 [Table Support] Table Configuration Modal Logic
    const [activeTableFieldIdx, setActiveTableFieldIdx] = useState(null);

    const openTableConfig = (idx) => {
        setActiveTableFieldIdx(idx);
    };

    const closeTableConfig = () => {
        setActiveTableFieldIdx(null);
    };

    const handleTableConfigSave = (fieldIndex, newItems) => {
        const newFields = [...editFields];
        newFields[fieldIndex] = { ...newFields[fieldIndex], items: newItems };
        setEditFields(newFields);
        setIsEditing(true);
        closeTableConfig();
    };

    // Sub-component for Table Configuration (Rendered locally for access to common styles)
    const TableConfigModal = ({ field, onSave, onCancel }) => {
        const [items, setItems] = useState(field.items || []);

        const addItem = () => {
            setItems([...items, { key: '', type: 'string', description: '' }]);
        };

        const updateItem = (idx, prop, val) => {
            const newItems = [...items];
            newItems[idx] = { ...newItems[idx], [prop]: val };
            setItems(newItems);
        };

        const removeItem = (idx) => {
            setItems(items.filter((_, i) => i !== idx));
        };

        return (
            <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                    <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">Table Configuration</h3>
                            <p className="text-xs text-gray-500">Define columns for table: <span className="font-mono text-gray-800">{field.key}</span></p>
                        </div>
                        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5">
                        <table className="w-full border-collapse">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-b">No.</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-b w-24">Type</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-b">Column Name (Key)</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-b">Description</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-b">Example Value</th>
                                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase border-b w-12">Del</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => (
                                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center text-xs text-gray-400">{idx + 1}</td>
                                        <td className="px-3 py-2">
                                            <select
                                                value={item.type}
                                                onChange={(e) => updateItem(idx, 'type', e.target.value)}
                                                className="w-full text-xs border-gray-200 rounded py-1 px-2 focus:ring-gray-600 focus:border-gray-600"
                                            >
                                                <option value="string">String</option>
                                                <option value="number">Number</option>
                                                <option value="date">Date</option>
                                                <option value="boolean">Boolean</option>
                                            </select>
                                        </td>
                                        <td className="px-3 py-2">
                                            <input
                                                type="text"
                                                value={item.key}
                                                onChange={(e) => updateItem(idx, 'key', e.target.value)}
                                                className="w-full text-sm border-transparent bg-transparent border-b focus:border-gray-600 focus:ring-0 px-0 py-1"
                                                placeholder="col_name"
                                            />
                                        </td>
                                        <td className="px-3 py-2">
                                            <input
                                                type="text"
                                                value={item.description}
                                                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                                className="w-full text-xs border-transparent bg-transparent border-b focus:border-gray-600 focus:ring-0 px-0 py-1 text-gray-500 placeholder-gray-300"
                                                placeholder="Description"
                                            />
                                        </td>
                                        <td className="px-3 py-2">
                                            <input
                                                type="text"
                                                value={item.exampleValue || ''}
                                                onChange={(e) => updateItem(idx, 'exampleValue', e.target.value)}
                                                className="w-full text-xs border-transparent bg-transparent border-b focus:border-gray-600 focus:ring-0 px-0 py-1 text-gray-500 placeholder-gray-300"
                                                placeholder="e.g. 10, Box"
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button onClick={() => removeItem(idx)} className="text-gray-400 hover:text-red-500">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="py-8 text-center text-gray-400 text-xs">
                                            No columns defined. Click "Add Column" to start.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <button
                            onClick={addItem}
                            className="mt-4 w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-500 hover:text-gray-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                        >
                            <Plus className="w-4 h-4" /> Add Column
                        </button>
                    </div>

                    <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 rounded-b-lg">
                        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded">Cancel</button>
                        <button onClick={() => onSave(items)} className="px-4 py-2 text-sm font-bold text-white bg-gray-800 hover:bg-gray-900 rounded shadow-sm">Save Configuration</button>
                    </div>
                </div>
            </div>
        );
    };

    // 🔄 Real-time Meta-Prompt sync: Update whenever fields change (regardless of tab)
    useEffect(() => {
        // Always keep the Meta-Prompt in sync with current fields
        loadPreviewPrompt();
    }, [loadPreviewPrompt]); // Now properly depends on the memoized function

    return (
        <div className={containerClasses}>
            {/* {isAutoSchemaModalOpen && <AutoSchemaModal />} */}
            {activeTableFieldIdx !== null && (
                <TableConfigModal
                    field={editFields[activeTableFieldIdx]}
                    onSave={(newItems) => handleTableConfigSave(activeTableFieldIdx, newItems)}
                    onCancel={closeTableConfig}
                />
            )}
            <div className={innerClasses}>
                <div className="flex flex-1 overflow-hidden">
                    {/* 사이드바: 템플릿 목록 (접힘/펼침) */}
                    {sidebarCollapsed ? (
                        /* ── 접힌 상태: 선택된 템플릿명 + 펼침 버튼 ── */
                        <div className="border-r border-gray-200 bg-gray-50 flex flex-col shrink-0 w-8">
                            <button
                                onClick={() => setSidebarCollapsed(false)}
                                className="h-full flex flex-col items-center py-2 gap-1 hover:bg-gray-100 transition-colors group"
                                title="템플릿 목록 펼치기"
                            >
                                <PanelLeftOpen className="w-4 h-4 text-gray-400 group-hover:text-gray-700 shrink-0" />
                                <span className="text-[10px] text-gray-500 group-hover:text-gray-800 font-medium [writing-mode:vertical-rl] tracking-wider truncate max-h-[200px]">
                                    {templates.find(t => t.id === selectedTemplateId)?.name || '템플릿'}
                                </span>
                            </button>
                        </div>
                    ) : (
                        /* ── 펼친 상태: 전체 사이드바 ── */
                        <div className="w-[156px] border-r border-gray-200 bg-gray-50 flex flex-col shrink-0">
                            <div className="p-2 border-b border-gray-200 flex items-center gap-1">
                                <button
                                    onClick={handleCreateNew}
                                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-800 text-white rounded hover:bg-gray-900 transition-colors shadow-sm font-bold text-xs"
                                >
                                    <Plus className="w-4 h-4" /> 새 템플릿
                                </button>
                                <button
                                    onClick={() => setSidebarCollapsed(true)}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                                    title="사이드바 접기"
                                >
                                    <PanelLeftClose className="w-4 h-4 text-gray-400" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto px-1.5 py-1.5 space-y-0.5">
                                {templates.map(template => (
                                    <button
                                        key={template.id}
                                        onClick={() => selectTemplate(template)}
                                        className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-all flex items-center ${selectedTemplateId === template.id
                                            ? 'bg-white text-gray-900 shadow-sm border-l-2 border-gray-800 font-bold'
                                            : 'text-gray-600 hover:bg-white hover:shadow-sm hover:text-gray-900 border-l-2 border-transparent'
                                            }`}
                                    >
                                        <span className="truncate flex-1">{template.name}</span>
                                        {template.source === 'db' && (
                                            <span className="flex items-center gap-0.5 ml-1">
                                                <span className="text-[8px] bg-amber-100 text-amber-700 px-1 rounded font-bold">DB</span>
                                                <Download
                                                    className="w-3.5 h-3.5 text-amber-600 hover:text-gray-800 cursor-pointer"
                                                    title="EXT 테이블로 가져오기"
                                                    onClick={(e) => handleImportLegacy(template, e)}
                                                />
                                            </span>
                                        )}
                                        {selectedTemplateId === template.id && (
                                            <span className="w-1.5 h-1.5 bg-gray-600 rounded-full ml-1"></span>
                                        )}
                                    </button>
                                ))}

                                {templates.length === 0 && (
                                    <div className="text-center py-10 text-gray-400 text-xs flex flex-col items-center">
                                        <LayoutTemplate className="w-8 h-8 opacity-20 mb-2" />
                                        <span>등록된 템플릿이 없습니다.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 메인 영역: 편집 */}
                    <div className="flex-1 flex flex-col bg-white min-w-0">
                        {/* 탭 헤더 */}
                        {/* 탭 헤더 */}
                        <div className="flex flex-col border-b border-gray-200">
                            <div className="flex">
                                <button
                                    onClick={() => setActiveTab('fields')}
                                    className={`px-4 py-2 text-xs font-bold border-b-2 transition-colors ${activeTab === 'fields'
                                        ? 'border-[#1f1f1f] text-[#1f1f1f]'
                                        : 'border-transparent text-[#5f6368] hover:text-[#1f1f1f]'
                                        }`}
                                >
                                    1. 필드 설정
                                </button>
                                <button
                                    onClick={() => setActiveTab('prompt')}
                                    className={`px-4 py-2 text-xs font-bold border-b-2 transition-colors ${activeTab === 'prompt'
                                        ? 'border-[#1f1f1f] text-[#1f1f1f]'
                                        : 'border-transparent text-[#5f6368] hover:text-[#1f1f1f]'
                                        }`}
                                >
                                    2. 프롬프트
                                </button>
                                <button
                                    onClick={() => setActiveTab('mergeRules')}
                                    className={`px-4 py-2 text-xs font-bold border-b-2 transition-colors ${activeTab === 'mergeRules'
                                        ? 'border-[#1f1f1f] text-[#1f1f1f]'
                                        : 'border-transparent text-[#5f6368] hover:text-[#1f1f1f]'
                                        }`}
                                >
                                    3. 머지 규칙
                                </button>
                                {/* 오른쪽 정렬: 도움말 + 닫기 */}
                                <div className="flex-1" />
                                <button
                                    onClick={() => setShowHelpModal(true)}
                                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                                    title="도움말"
                                >
                                    <HelpCircle className="w-4 h-4 text-gray-400" />
                                </button>
                                {onClose && (
                                    <button
                                        onClick={onClose}
                                        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors ml-0.5"
                                        title="닫기"
                                    >
                                        <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {activeTab === 'fields' ? (
                            <>
                                {/* 통합 툴바 (1줄) */}
                                <div className="px-3 py-1.5 border-b border-gray-200 flex items-center gap-2 shrink-0 bg-white">
                                    {/* Mode Badge */}
                                    <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0 ${selectedTemplateId
                                        ? 'bg-gray-800 text-white'
                                        : 'bg-gray-800 text-white'
                                        }`}>
                                        {selectedTemplateId ? 'EDIT' : 'NEW'}
                                    </div>

                                    {/* 템플릿 이름 */}
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => { setEditName(e.target.value); setIsEditing(true); }}
                                        className="w-40 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-gray-600 focus:border-gray-600 text-xs font-bold text-gray-800 placeholder-gray-300"
                                        placeholder="템플릿 이름"
                                        title={selectedTemplateId ? `ID: ${selectedTemplateId}` : ''}
                                    />

                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        accept="image/*,.pdf"
                                    />
                                    <button
                                        onClick={handleExtractFromCurrent}
                                        disabled={isAutoGenerating}
                                        className="flex items-center gap-1 px-2.5 py-1 bg-gray-800 text-white rounded hover:bg-gray-900 text-[11px] font-bold shadow-sm transition-all shrink-0"
                                    >
                                        {isAutoGenerating ? `${autoGenProgress}%` : <><Sparkles className="w-3 h-3" /> 자동분석</>}
                                    </button>

                                    <button
                                        onClick={addCustomField}
                                        className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 text-gray-600 rounded hover:bg-gray-50 text-[11px] font-medium transition-colors shrink-0"
                                    >
                                        <Plus className="w-3 h-3" /> 단일필드 추가
                                    </button>

                                    {/* 🏷️ 라벨링 버튼 */}
                                    {selectedTemplateId && (
                                        <button
                                            onClick={() => {
                                                let imageUrl = null;
                                                let fileName = null;
                                                if (currentDocumentImage) {
                                                    imageUrl = currentDocumentImage.startsWith('data:')
                                                        ? currentDocumentImage
                                                        : `data:image/png;base64,${currentDocumentImage}`;
                                                    if (selectedFile) {
                                                        fileName = selectedFile.name || (selectedFile.file && selectedFile.file.name);
                                                    }
                                                } else {
                                                    const template = templates.find(t => t.id === selectedTemplateId);
                                                    const samplePath = template?.sampleImage;
                                                    if (samplePath) {
                                                        const isPdf = samplePath.toLowerCase().endsWith('.pdf');
                                                        imageUrl = isPdf
                                                            ? `${apiBaseUrl}${samplePath}?format=png`
                                                            : `${apiBaseUrl}${samplePath}`;
                                                        fileName = samplePath.split(/[/\\]/).pop();
                                                    }
                                                }
                                                setLabelingSampleImageUrl(imageUrl);
                                                setLabelingSampleFileName(fileName);
                                                setShowLabelingModal(true);
                                            }}
                                            className="flex items-center gap-1 px-2 py-1 bg-[#f1f3f4] border border-[#dadce0] text-[#444746] rounded hover:bg-[#e8eaed] text-[11px] font-medium transition-colors shrink-0"
                                        >
                                            <Tag className="w-3 h-3" /> 라벨링
                                        </button>
                                    )}

                                    {selectedFields.length > 0 && (
                                        <button
                                            onClick={removeSelectedFields}
                                            className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 text-red-600 rounded hover:bg-red-100 text-[11px] font-medium transition-colors shrink-0"
                                        >
                                            <Trash2 className="w-3 h-3" /> {selectedFields.length}개 삭제
                                        </button>
                                    )}

                                    {selectedTableColumns.length > 0 && (
                                        <button
                                            onClick={removeSelectedTableColumns}
                                            className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 text-gray-600 rounded hover:bg-gray-100 text-[11px] font-medium transition-colors shrink-0"
                                        >
                                            <Trash2 className="w-3 h-3" /> 컬럼 {selectedTableColumns.length}개
                                        </button>
                                    )}

                                    {/* 오른쪽 정렬 (취소/삭제/저장) */}
                                    <div className="flex-1" />

                                    {!selectedTemplateId && (
                                        <button
                                            onClick={() => {
                                                if (templates.length > 0) {
                                                    selectTemplate(templates[0]);
                                                }
                                                setIsEditing(false);
                                            }}
                                            className="px-2 py-1 text-gray-500 hover:bg-gray-100 rounded text-[11px] font-medium transition-colors shrink-0"
                                        >
                                            취소
                                        </button>
                                    )}
                                    {selectedTemplateId && (
                                        <>
                                            <button
                                                onClick={handleCopyTemplate}
                                                disabled={loading}
                                                className="flex items-center gap-1 px-2 py-1 text-gray-500 hover:bg-gray-100 rounded text-[11px] font-medium transition-colors shrink-0"
                                                title="현재 템플릿 복사"
                                            >
                                                <Copy className="w-3 h-3" /> 복사
                                            </button>
                                            <button
                                                onClick={handleDelete}
                                                className="px-2 py-1 text-red-500 hover:bg-red-50 rounded text-[11px] font-medium transition-colors shrink-0"
                                            >
                                                삭제
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={handleSave}
                                        disabled={!isEditing || loading}
                                        className={`flex items-center gap-1 px-3 py-1 rounded text-[11px] font-bold transition-all shrink-0 ${isEditing
                                            ? 'bg-gray-800 text-white hover:bg-gray-900'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        {loading ? '...' : <><Save className="w-3 h-3" /> 저장</>}
                                    </button>
                                </div>

                                {/* 필드 테이블 영역 */}
                                <div className="flex-1 overflow-auto bg-white">
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                        modifiers={[restrictToVerticalAxis]}
                                    >
                                        <table className="w-full border-collapse">
                                            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                                <tr>
                                                    <th className={`${thClass} w-10 text-center`}>No.</th>
                                                    <th className={`${thClass} w-10 text-center`}>
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-gray-300 text-gray-800 focus:ring-gray-600"
                                                            checked={editFields.length > 0 && selectedFields.length === editFields.length}
                                                            onChange={toggleAllSelection}
                                                        />
                                                    </th>
                                                    <th className={`${thClass} w-20`}>
                                                        <div className="flex items-center gap-1">
                                                            Type
                                                            <div className="relative group">
                                                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                                                                <div className="absolute left-0 top-5 w-48 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                                                    데이터 타입: String, Number, Date, Boolean, Table 등
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th className={thClass}>
                                                        <div className="flex items-center gap-1">
                                                            Field Key
                                                            <div className="relative group">
                                                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                                                                <div className="absolute left-0 top-5 w-52 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                                                    OCR 추출 시 사용하는 키값 (영문 권장)
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th className={`${thClass} text-gray-800`}>
                                                        <div className="flex items-center gap-1">
                                                            출력명
                                                            <div className="relative group">
                                                                <HelpCircle className="w-3 h-3 text-gray-500 cursor-help" />
                                                                <div className="absolute left-0 top-5 w-56 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                                                    그리드 헤더 및 Excel 컬럼명으로 표시됩니다. 비우면 Field Key 사용
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </th>

                                                    <th className={thClass}>
                                                        <div className="flex items-center gap-1">
                                                            Description
                                                            <div className="relative group">
                                                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                                                                <div className="absolute left-0 top-5 w-48 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                                                    AI 프롬프트에 포함되는 필드 설명
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th className={thClass}>Example</th>
                                                    <th className={`${thClass} w-12 text-center`} title="Primary Key: 이 필드로 매핑합니다">PK</th>
                                                    <th className={`${thClass} w-16 text-center relative group`}>
                                                        <span className="cursor-help">🔄</span>
                                                        <div className="absolute left-0 top-full mt-1 w-48 bg-gray-800 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg text-left">
                                                            <div className="font-bold mb-2 border-b border-gray-600 pb-1">머지 역할</div>
                                                            <div className="space-y-1.5">
                                                                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-gray-300 shrink-0"></span> 없음</div>
                                                                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-purple-500 shrink-0"></span> 매칭 키</div>
                                                                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-gray-600 shrink-0"></span> 메인 식별자</div>
                                                                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-gray-400 shrink-0"></span> 서브 식별자</div>
                                                            </div>
                                                            <div className="mt-2 pt-1.5 border-t border-gray-600 text-gray-300 text-[10px]">
                                                                클릭하여 빠른 설정
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th className={`${thClass} w-12 text-center`} title="순서 변경">↕</th>
                                                    <th className={`${thClass} w-12 text-center`}>Del</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <SortableContext
                                                    items={unifiedFields.map(f => f.id)}
                                                    strategy={verticalListSortingStrategy}
                                                >
                                                    {unifiedFields.map((field, visualIndex) => {
                                                        const isSingle = field.type === 'single';
                                                        const idx = field.originalIndex;

                                                        return (
                                                            <SortableRowWrapper
                                                                key={field.id}
                                                                id={field.id}
                                                                className={`hover:bg-gray-100/50 transition-colors group ${isSingle && selectedFields.includes(field.key) ? 'bg-gray-100' : ''} ${((field.type === 'single' && field.outputOrder === 0) || (field.type === 'computed' && (field.outputOrder === 0 || field.order === 0))) ? 'opacity-50' : ''}`}
                                                            >
                                                                {/* No. Column with Drag Handle */}
                                                                <td className={`${tdClass} text-center text-gray-500 text-xs relative group-hover:text-gray-800`}>
                                                                    <div className="flex items-center justify-center h-full w-full">
                                                                        <span className="group-hover:hidden">{visualIndex + 1}</span>
                                                                        <div className="hidden group-hover:flex absolute inset-0 items-center justify-center bg-white/80">
                                                                            <DragHandle />
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {isSingle ? (
                                                                    <>
                                                                        <td className={`${tdClass} text-center`}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedFields.includes(field.key)}
                                                                                onClick={(e) => toggleFieldSelection(field.key, e, visualIndex)}
                                                                                onChange={() => { }} // Use onClick for Shift key support; strictly controlled
                                                                                className="rounded border-gray-300 text-gray-800 focus:ring-gray-600"
                                                                            />
                                                                        </td>
                                                                        <td className={tdClass}>
                                                                            <select
                                                                                value={field.type}
                                                                                onChange={(e) => handleFieldChange(idx, 'type', e.target.value)}
                                                                                className="w-full bg-white border border-gray-200 rounded px-2 py-1 text-xs font-medium text-gray-700 focus:ring-gray-600 focus:border-gray-600"
                                                                            >
                                                                                <option value="string">String</option>
                                                                                <option value="number">Number</option>
                                                                                <option value="date">Date</option>
                                                                                <option value="boolean">Boolean</option>
                                                                                <option value="array">Table</option>
                                                                            </select>
                                                                        </td>
                                                                        <td className={tdClass}>
                                                                            <input
                                                                                type="text"
                                                                                value={field.key}
                                                                                onChange={(e) => handleFieldChange(idx, 'key', e.target.value)}
                                                                                className="w-full bg-transparent border-b border-transparent focus:border-gray-600 p-0 text-xs font-medium text-gray-800 focus:ring-0"
                                                                                placeholder="Field Key"
                                                                            />
                                                                        </td>
                                                                        <td className={tdClass}>
                                                                            <input
                                                                                type="text"
                                                                                value={field.displayName || ''}
                                                                                onChange={(e) => handleFieldChange(idx, 'displayName', e.target.value)}
                                                                                className="w-full bg-gray-100 border border-gray-300 rounded px-1 py-0.5 text-xs font-medium text-gray-800 focus:ring-1 focus:ring-gray-600"
                                                                                placeholder={field.key || '출력명'}
                                                                            />
                                                                        </td>

                                                                        <td className={tdClass}>
                                                                            {field.type === 'array' ? (
                                                                                <button
                                                                                    onClick={() => openTableConfig(idx)}
                                                                                    className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded hover:bg-gray-200 flex items-center gap-1"
                                                                                >
                                                                                    <LayoutTemplate className="w-3 h-3" /> Config Table
                                                                                </button>
                                                                            ) : (
                                                                                <input
                                                                                    type="text"
                                                                                    value={field.description}
                                                                                    onChange={(e) => handleFieldChange(idx, 'description', e.target.value)}
                                                                                    className="w-full bg-transparent border-b border-transparent focus:border-gray-600 p-0 text-xs text-gray-500 focus:ring-0 placeholder-gray-300"
                                                                                    placeholder="Description"
                                                                                />
                                                                            )}
                                                                        </td>
                                                                        <td className={tdClass}>
                                                                            <div className="flex items-center gap-0.5">
                                                                                <input
                                                                                    type="text"
                                                                                    value={exampleValues[field.key] || ''}
                                                                                    onChange={(e) => {
                                                                                        setExampleValues(prev => ({ ...prev, [field.key]: e.target.value }));
                                                                                        setIsEditing(true);
                                                                                    }}
                                                                                    className="flex-1 min-w-0 bg-transparent border-b border-transparent focus:border-gray-600 p-0 text-xs text-gray-600 focus:ring-0 placeholder-gray-300"
                                                                                    placeholder="예: 값1, 값2"
                                                                                />
                                                                                <button
                                                                                    onClick={() => setExampleExpandModal({ isOpen: true, fieldType: 'single', fieldKey: field.key, value: exampleValues[field.key] || '', tableIdx: -1, colIdx: -1 })}
                                                                                    className="p-0.5 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                                                    title="확대 편집"
                                                                                >
                                                                                    <Maximize2 size={11} />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                        <td className={`${tdClass} text-center`}>
                                                                            <input
                                                                                type="radio"
                                                                                name="primaryKey"
                                                                                checked={editPrimaryKey === field.key}
                                                                                onChange={() => { }} // React 경고 방지용 (실제 로직은 onClick에서)
                                                                                onClick={() => {
                                                                                    // 토글: 이미 선택된 PK 클릭 시 해제, 아니면 설정
                                                                                    if (editPrimaryKey === field.key) {
                                                                                        setEditPrimaryKey('');
                                                                                    } else {
                                                                                        setEditPrimaryKey(field.key);
                                                                                    }
                                                                                    setIsEditing(true);
                                                                                }}
                                                                                className="text-gray-800 focus:ring-gray-600 cursor-pointer"
                                                                                disabled={field.type === 'array'}
                                                                                title="클릭: PK 설정 / 다시 클릭: PK 해제"
                                                                            />
                                                                        </td>
                                                                        <td
                                                                            className={`${tdClass} text-center cursor-pointer hover:bg-gray-100`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setMergeRolePopup({
                                                                                    show: true,
                                                                                    fieldKey: field.key,
                                                                                    x: e.clientX,
                                                                                    y: e.clientY
                                                                                });
                                                                            }}
                                                                            title="클릭하여 머지 역할 설정"
                                                                        >
                                                                            <div className="flex items-center justify-center gap-1">
                                                                                {getMergeRoleIcons(field.key).length > 0 ? (
                                                                                    getMergeRoleIcons(field.key).map((roleIcon, idx) => (
                                                                                        <span
                                                                                            key={idx}
                                                                                            title={roleIcon.title}
                                                                                            className={`w-2.5 h-2.5 rounded-full ${roleIcon.bgColor}`}
                                                                                        />
                                                                                    ))
                                                                                ) : (
                                                                                    <span className="w-2.5 h-2.5 rounded-full bg-gray-300" title="없음" />
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td className={`${tdClass} text-center`}>
                                                                            <DragHandle />
                                                                        </td>
                                                                        <td className={`${tdClass} text-center`}>
                                                                            <button
                                                                                onClick={() => removeField(field.key)}
                                                                                className="p-1 text-gray-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            >
                                                                                <X className="w-4 h-4" />
                                                                            </button>
                                                                        </td>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <td className={`${tdClass} text-center`}><span className="text-gray-300">-</span></td>
                                                                        <td className={tdClass}><span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold">Computed</span></td>
                                                                        <td className={tdClass}>
                                                                            <input
                                                                                type="text"
                                                                                value={field.key}
                                                                                onChange={(e) => updateComputedField(idx, 'key', e.target.value)}
                                                                                className="w-full bg-transparent border-b border-transparent focus:border-gray-500 p-0 text-xs font-medium text-gray-800"
                                                                                placeholder="출력 Key"
                                                                            />
                                                                        </td>
                                                                        <td className={tdClass}>
                                                                            <input
                                                                                type="text"
                                                                                value={field.displayName}
                                                                                onChange={(e) => updateComputedField(idx, 'displayName', e.target.value)}
                                                                                className="w-full bg-gray-100 border border-gray-300 rounded px-1 py-0.5 text-xs font-medium text-gray-800"
                                                                                placeholder="출력명"
                                                                            />
                                                                        </td>

                                                                        <td className={tdClass} colSpan="1">
                                                                            <input
                                                                                type="text"
                                                                                value={field.formula}
                                                                                onChange={(e) => {
                                                                                    updateComputedField(idx, 'formula', e.target.value);
                                                                                    updateComputedField(idx, 'sourceFields', parseFormulaSourceFields(e.target.value));
                                                                                }}
                                                                                className="w-full bg-white border border-gray-300 rounded px-2 py-0.5 text-xs font-mono text-gray-700"
                                                                                placeholder="{MODEL} {Year}"
                                                                            />
                                                                        </td>
                                                                        <td className={`${tdClass} text-center`}><span className="text-gray-300">-</span></td>
                                                                        <td className={`${tdClass} text-center`}><span className="text-gray-300">-</span></td>
                                                                        <td className={`${tdClass} text-center`}>
                                                                            <DragHandle />
                                                                        </td>
                                                                        <td className={`${tdClass} text-center`}>
                                                                            <button
                                                                                onClick={() => removeComputedField(idx)}
                                                                                className="p-1 text-gray-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100"
                                                                            >
                                                                                <X className="w-4 h-4" />
                                                                            </button>
                                                                        </td>
                                                                    </>
                                                                )}
                                                            </SortableRowWrapper>
                                                        );
                                                    })}
                                                </SortableContext>

                                                {unifiedFields.length === 0 && (
                                                    <tr>
                                                        <td colSpan="10" className="py-12 text-center text-gray-400">
                                                            <div className="flex flex-col items-center justify-center">
                                                                <LayoutTemplate className="w-8 h-8 mb-2 opacity-20" />
                                                                <p className="text-xs">등록된 필드가 없습니다.</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                                {/* Table Groups */}
                                                {editTableFields.map((table, tableIdx) => (
                                                    <React.Fragment key={`table-${tableIdx}`}>
                                                        <tr className="bg-gray-100 border-t-2 border-gray-300">
                                                            <td colSpan="10" className="py-2 px-4">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-2">
                                                                        <LayoutTemplate className="w-4 h-4 text-gray-500" />
                                                                        <input
                                                                            type="text"
                                                                            value={table.tableName}
                                                                            onChange={(e) => updateTableGroupName(tableIdx, 'tableName', e.target.value)}
                                                                            className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-gray-600 text-sm font-bold text-gray-700 focus:outline-none"
                                                                            placeholder="테이블 이름"
                                                                        />
                                                                        <span className="text-xs text-gray-400">({table.columns.length}개 컬럼)</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            onClick={() => addTableColumn(tableIdx)}
                                                                            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                                                                        >
                                                                            <Plus className="w-3 h-3" /> 컬럼
                                                                        </button>
                                                                        <button
                                                                            onClick={() => removeTableGroup(tableIdx)}
                                                                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                                                                        >
                                                                            <X className="w-4 h-4" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {table.columns.map((col, colIdx) => (
                                                            <tr key={`${tableIdx}-${colIdx}`} className={`hover:bg-gray-100/50 transition-colors group ${selectedTableColumns.includes(`${tableIdx}-${colIdx}`) ? 'bg-gray-200' : 'bg-gray-50/30'}`}>
                                                                <td className={`${tdClass} text-center text-gray-400 text-xs`}>T-{colIdx + 1}</td>
                                                                <td className={`${tdClass} text-center`}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedTableColumns.includes(`${tableIdx}-${colIdx}`)}
                                                                        onChange={() => toggleTableColumnSelection(tableIdx, colIdx)}
                                                                        className="w-4 h-4 accent-gray-800 cursor-pointer"
                                                                    />
                                                                </td>
                                                                <td className={tdClass}>
                                                                    <select value={col.type} onChange={(e) => updateTableColumn(tableIdx, colIdx, 'type', e.target.value)} className="w-full bg-white border border-gray-200 rounded px-2 py-1 text-xs font-medium text-gray-700">
                                                                        <option value="string">String</option>
                                                                        <option value="number">Number</option>
                                                                        <option value="date">Date</option>
                                                                        <option value="computed">Computed</option>
                                                                    </select>
                                                                </td>
                                                                <td className={tdClass}>
                                                                    <input type="text" value={col.key} onChange={(e) => updateTableColumn(tableIdx, colIdx, 'key', e.target.value)} className="w-full bg-transparent border-b border-transparent focus:border-gray-600 p-0 text-xs font-medium text-gray-800" placeholder="Column Key" />
                                                                </td>
                                                                <td className={tdClass}>
                                                                    <input type="text" value={col.displayName || ''} onChange={(e) => updateTableColumn(tableIdx, colIdx, 'displayName', e.target.value)} className="w-full bg-gray-100 border border-gray-300 rounded px-1 py-0.5 text-xs font-medium text-gray-800" placeholder={col.key || '출력명'} />
                                                                </td>

                                                                {col.type === 'computed' ? (
                                                                    <td className={tdClass} colSpan="2">
                                                                        <input
                                                                            type="text"
                                                                            value={col.formula || ''}
                                                                            onChange={(e) => updateTableColumn(tableIdx, colIdx, 'formula', e.target.value)}
                                                                            className="w-full bg-white border border-gray-300 rounded px-2 py-0.5 text-xs font-mono text-gray-700"
                                                                            placeholder="{Qty} * {Price} or {Model} {Year}"
                                                                        />
                                                                    </td>
                                                                ) : (
                                                                    <>
                                                                        <td className={tdClass}>
                                                                            <input type="text" value={col.description || ''} onChange={(e) => updateTableColumn(tableIdx, colIdx, 'description', e.target.value)} className="w-full bg-transparent border-b border-transparent focus:border-gray-600 p-0 text-xs text-gray-500" placeholder="Description" />
                                                                        </td>
                                                                        <td className={tdClass}>
                                                                            <div className="flex items-center gap-0.5">
                                                                                <input type="text" value={col.example || ''} onChange={(e) => updateTableColumn(tableIdx, colIdx, 'example', e.target.value)} className="flex-1 min-w-0 bg-transparent border-b border-transparent focus:border-gray-600 p-0 text-xs text-gray-500" placeholder="Example" />
                                                                                <button
                                                                                    onClick={() => setExampleExpandModal({ isOpen: true, fieldType: 'table', fieldKey: col.key, value: col.example || '', tableIdx, colIdx })}
                                                                                    className="p-0.5 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                                                    title="확대 편집"
                                                                                >
                                                                                    <Maximize2 size={11} />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </>
                                                                )}

                                                                {/* PK 라디오 버튼 - 테이블 컬럼에도 PK 설정 가능 (클릭하면 토글) */}
                                                                <td className={`${tdClass} text-center`}>
                                                                    <input
                                                                        type="radio"
                                                                        name={`tablePK-${tableIdx}`}
                                                                        checked={col.isPrimaryKey || false}
                                                                        onChange={() => { }} // React 경고 방지용 (실제 로직은 onClick에서)
                                                                        onClick={() => {
                                                                            // 토글: 이미 선택된 PK 클릭 시 해제, 아니면 설정
                                                                            setEditTableFields(prev => {
                                                                                const newTables = [...prev];
                                                                                if (col.isPrimaryKey) {
                                                                                    // 해제
                                                                                    const newColumns = [...newTables[tableIdx].columns];
                                                                                    newColumns[colIdx] = { ...newColumns[colIdx], isPrimaryKey: false };
                                                                                    newTables[tableIdx] = { ...newTables[tableIdx], columns: newColumns };
                                                                                } else {
                                                                                    // 설정 (다른 컬럼 해제)
                                                                                    const newColumns = newTables[tableIdx].columns.map((c, idx) => ({
                                                                                        ...c,
                                                                                        isPrimaryKey: idx === colIdx
                                                                                    }));
                                                                                    newTables[tableIdx] = { ...newTables[tableIdx], columns: newColumns };
                                                                                }
                                                                                return newTables;
                                                                            });
                                                                            setIsEditing(true);
                                                                        }}
                                                                        className="text-gray-800 focus:ring-gray-600 cursor-pointer"
                                                                        title="클릭: PK 설정 / 다시 클릭: PK 해제"
                                                                    />
                                                                </td>
                                                                {/* 머지 역할 (테이블 컬럼) - 클릭하여 Match Key 등 설정 */}
                                                                <td
                                                                    className={`${tdClass} text-center cursor-pointer hover:bg-gray-100`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        // 테이블 컬럼용 머지 역할 팝업 표시
                                                                        setMergeRolePopup({
                                                                            show: true,
                                                                            fieldKey: col.key,
                                                                            x: e.clientX,
                                                                            y: e.clientY
                                                                        });
                                                                    }}
                                                                    title="클릭하여 머지 역할 설정 (Match Key 등)"
                                                                >
                                                                    <div className="flex items-center justify-center gap-1">
                                                                        {getMergeRoleIcons(col.key).length > 0 ? (
                                                                            getMergeRoleIcons(col.key).map((roleIcon, idx) => (
                                                                                <span
                                                                                    key={idx}
                                                                                    title={roleIcon.title}
                                                                                    className={`w-2.5 h-2.5 rounded-full ${roleIcon.bgColor}`}
                                                                                />
                                                                            ))
                                                                        ) : (
                                                                            <span className="w-2.5 h-2.5 rounded-full bg-gray-300" title="없음" />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className={`${tdClass} text-center`}>
                                                                    <div className="flex items-center justify-center gap-0.5">
                                                                        <button
                                                                            onClick={() => moveTableColumn(tableIdx, colIdx, 'up')}
                                                                            disabled={colIdx === 0}
                                                                            className={`p-0.5 rounded ${colIdx === 0 ? 'text-gray-200' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-200'}`}
                                                                        >
                                                                            <ChevronUp className="w-3 h-3" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => moveTableColumn(tableIdx, colIdx, 'down')}
                                                                            disabled={colIdx === table.columns.length - 1}
                                                                            className={`p-0.5 rounded ${colIdx === table.columns.length - 1 ? 'text-gray-200' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-200'}`}
                                                                        >
                                                                            <ChevronDown className="w-3 h-3" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                                <td className={`${tdClass} text-center`}>
                                                                    <button onClick={() => removeTableColumn(tableIdx, colIdx)} className="p-1 text-gray-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100"><X className="w-4 h-4" /></button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                                <tr className="bg-gray-50 border-t border-dashed border-gray-300">
                                                    <td colSpan="10" className="py-2 px-4">
                                                        <button onClick={addTableGroup} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800">
                                                            <Plus className="w-3 h-3" /> 테이블 추가
                                                        </button>
                                                    </td>
                                                </tr>
                                                <tr className="bg-gray-100 border-t-2 border-gray-300">
                                                    <td colSpan="10" className="py-2 px-4">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <Sparkles className="w-4 h-4 text-gray-500" />
                                                                <span className="text-sm font-bold text-gray-700">합성 필드 만들기</span>
                                                                <span className="text-xs text-gray-500">위 리스트에 추가됩니다</span>
                                                            </div>
                                                            <button
                                                                onClick={addComputedField}
                                                                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                                                            >
                                                                <Plus className="w-3 h-3" /> 합성 필드 추가
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </DndContext>
                                </div>
                            </>
                        ) : activeTab === 'prompt' ? (
                            <div className="flex-1 flex flex-col overflow-hidden bg-white p-2">
                                {/* 사용자 프롬프트 편집 (시스템 프롬프트는 서버 전용) */}
                                <div className="bg-white rounded border border-[#dadce0] flex-1 flex flex-col min-h-0">
                                    {/* 헤더 */}
                                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#dadce0] shrink-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-semibold text-[#1f1f1f]">사용자 프롬프트</span>
                                            <span className="text-[10px] text-[#80868b]">(필드별 설명/예시 편집)</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => {
                                                    if (window.confirm('자동 생성된 프롬프트로 초기화할까요?\n현재 편집 내용이 사라집니다.')) {
                                                        setCustomPrompt('');
                                                        setSystemPrompt('');
                                                        setIsEditing(true);
                                                    }
                                                }}
                                                className="px-2 py-1 text-[11px] text-[#5f6368] hover:text-[#1f1f1f] hover:bg-[#f1f3f4] rounded transition-colors"
                                            >
                                                초기화
                                            </button>
                                            <button
                                                onClick={() => setIsMetaPromptPopupOpen(true)}
                                                className="px-2 py-1 text-[11px] text-[#5f6368] hover:text-[#1f1f1f] hover:bg-[#f1f3f4] rounded transition-colors"
                                                title="전체 프롬프트 미리보기"
                                            >
                                                미리보기
                                            </button>
                                            <button
                                                onClick={handleSave}
                                                disabled={!isEditing || loading}
                                                className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${isEditing
                                                    ? 'bg-[#1f1f1f] text-white hover:bg-[#3c4043]'
                                                    : 'bg-[#f1f3f4] text-[#80868b] cursor-not-allowed'
                                                    }`}
                                            >
                                                {loading ? '저장...' : '저장'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* 안내 */}
                                    <div className="px-3 py-1.5 border-b border-[#f1f3f4] text-[10px] text-[#5f6368] shrink-0 leading-relaxed">
                                        각 필드의 <b>설명</b>과 <b>예시</b>를 수정하면 AI 추출 프롬프트에 자동 반영됩니다. 시스템 프롬프트(공통 규칙)는 서버에서 관리됩니다.
                                    </div>

                                    {/* 필드 편집 리스트 */}
                                    <div className="flex-1 overflow-auto min-h-0">
                                        {/* 컬럼 헤더 */}
                                        <div className="sticky top-0 z-10 bg-[#f8f9fa] border-b border-[#dadce0] grid grid-cols-[110px_1fr_1fr] gap-0 px-1">
                                            <div className="px-3 py-2 text-[11px] font-medium text-[#5f6368]">Key</div>
                                            <div className="px-3 py-2 text-[11px] font-medium text-[#5f6368]">설명(Description)</div>
                                            <div className="px-3 py-2 text-[11px] font-medium text-[#5f6368]">예시값(Example)</div>
                                        </div>

                                        {/* 단일 필드 — outputOrder 순서대로 표시 (필드설정 탭과 동일) */}
                                        {editFields
                                            .map((field, index) => ({ field, origIndex: index }))
                                            .sort((a, b) => {
                                                const orderA = (typeof a.field === 'object' ? a.field.outputOrder : undefined) || 999;
                                                const orderB = (typeof b.field === 'object' ? b.field.outputOrder : undefined) || 999;
                                                return orderA - orderB;
                                            })
                                            .map(({ field, origIndex }) => {
                                            const fieldKey = typeof field === 'string' ? field : field.key;
                                            const fieldType = typeof field === 'object' ? field.type : 'string';
                                            const fieldDesc = typeof field === 'object' ? (field.description || '') : '';
                                            const fieldExample = exampleValues[fieldKey] || (typeof field === 'object' ? (field.exampleValue || field.example || '') : '');
                                            return (
                                                <div key={`sf-${origIndex}`} className="grid grid-cols-[110px_1fr_1fr] gap-0 px-1 border-b border-[#f1f3f4] hover:bg-[#f8f9fa] items-start">
                                                    <div className="px-3 py-2.5">
                                                        <span className="font-mono text-[11px] font-semibold text-[#1f1f1f] break-all leading-tight">{fieldKey}</span>
                                                        {fieldType && fieldType !== 'string' && (
                                                            <span className="block text-[10px] text-[#80868b] mt-0.5">{fieldType}</span>
                                                        )}
                                                    </div>
                                                    <div className="px-1.5 py-1.5">
                                                        <textarea
                                                            value={fieldDesc}
                                                            onChange={(e) => {
                                                                const updated = [...editFields];
                                                                if (typeof updated[origIndex] === 'string') {
                                                                    updated[origIndex] = { key: updated[origIndex], type: 'string', description: e.target.value };
                                                                } else {
                                                                    updated[origIndex] = { ...updated[origIndex], description: e.target.value };
                                                                }
                                                                setEditFields(updated);
                                                                setIsEditing(true);
                                                            }}
                                                            rows={2}
                                                            className="w-full px-2 py-1.5 text-xs border border-[#dadce0] rounded focus:border-[#1f1f1f] focus:ring-0 outline-none bg-white text-[#3c4043] placeholder-[#80868b] resize-y min-h-[36px]"
                                                            placeholder="AI에게 전달할 설명"
                                                        />
                                                    </div>
                                                    <div className="px-1.5 py-1.5">
                                                        <textarea
                                                            value={fieldExample}
                                                            onChange={(e) => {
                                                                setExampleValues(prev => ({ ...prev, [fieldKey]: e.target.value }));
                                                                setIsEditing(true);
                                                            }}
                                                            rows={2}
                                                            className="w-full px-2 py-1.5 text-xs font-mono border border-[#dadce0] rounded focus:border-[#1f1f1f] focus:ring-0 outline-none bg-white text-[#3c4043] placeholder-[#80868b] resize-y min-h-[36px]"
                                                            placeholder="예시 값"
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* 테이블 필드 구분 */}
                                        {editTableFields.length > 0 && (
                                            <div className="bg-[#f1f3f4] px-4 py-1.5 text-[10px] font-medium text-[#5f6368] uppercase tracking-wider">
                                                TABLE COLUMNS
                                            </div>
                                        )}

                                        {/* 테이블 컬럼 */}
                                        {editTableFields.map((table, tableIndex) => (
                                            (table.columns || []).map((col, colIndex) => {
                                                const colKey = typeof col === 'string' ? col : col.key;
                                                const colDesc = typeof col === 'object' ? (col.description || '') : '';
                                                const colExample = exampleValues[colKey] || (typeof col === 'object' ? (col.exampleValue || col.example || '') : '');
                                                return (
                                                    <div key={`tc-${tableIndex}-${colIndex}`} className="grid grid-cols-[110px_1fr_1fr] gap-0 px-1 border-b border-[#f1f3f4] hover:bg-[#f8f9fa] items-start">
                                                        <div className="px-3 py-2.5">
                                                            <span className="font-mono text-[11px] font-semibold text-[#1f1f1f] break-all leading-tight">{colKey}</span>
                                                        </div>
                                                        <div className="px-1.5 py-1.5">
                                                            <textarea
                                                                value={colDesc}
                                                                onChange={(e) => {
                                                                    const updated = JSON.parse(JSON.stringify(editTableFields));
                                                                    if (typeof updated[tableIndex].columns[colIndex] === 'string') {
                                                                        updated[tableIndex].columns[colIndex] = { key: updated[tableIndex].columns[colIndex], type: 'string', description: e.target.value };
                                                                    } else {
                                                                        updated[tableIndex].columns[colIndex] = { ...updated[tableIndex].columns[colIndex], description: e.target.value };
                                                                    }
                                                                    setEditTableFields(updated);
                                                                    setIsEditing(true);
                                                                }}
                                                                rows={2}
                                                                className="w-full px-2 py-1.5 text-xs border border-[#dadce0] rounded focus:border-[#1f1f1f] focus:ring-0 outline-none bg-white text-[#3c4043] placeholder-[#80868b] resize-y min-h-[36px]"
                                                                placeholder="AI에게 전달할 설명"
                                                            />
                                                        </div>
                                                        <div className="px-1.5 py-1.5">
                                                            <textarea
                                                                value={colExample}
                                                                onChange={(e) => {
                                                                    setExampleValues(prev => ({ ...prev, [colKey]: e.target.value }));
                                                                    setIsEditing(true);
                                                                }}
                                                                rows={2}
                                                                className="w-full px-2 py-1.5 text-xs font-mono border border-[#dadce0] rounded focus:border-[#1f1f1f] focus:ring-0 outline-none bg-white text-[#3c4043] placeholder-[#80868b] resize-y min-h-[36px]"
                                                                placeholder="예시 값"
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ))}

                                        {/* 빈 상태 */}
                                        {editFields.length === 0 && editTableFields.length === 0 && (
                                            <div className="px-4 py-8 text-center text-[#80868b] text-xs">
                                                필드를 추가하면 여기에 표시됩니다.
                                            </div>
                                        )}
                                    </div>

                                    {/* 하단 상태바 */}
                                    <div className="px-3 py-1.5 border-t border-[#dadce0] flex items-center justify-between shrink-0 bg-[#f8f9fa]">
                                        <span className="text-[10px] text-[#80868b]">
                                            {editFields.length}개 단일 + {editTableFields.reduce((acc, t) => acc + ((t.columns || []).length), 0)}개 테이블 컬럼
                                        </span>
                                        {isEditing && (
                                            <span className="text-[10px] text-[#1f1f1f] font-medium">변경사항 있음</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : activeTab === 'mergeRules' ? (
                            /* 📄 머지 규칙 설정 탭 - PK 기반 UI */
                            <div className="flex-1 flex flex-col overflow-hidden bg-white p-6">
                                {/* 헤더 + 도움말 버튼 */}
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-gray-800">📄 문서 머지 규칙</h3>
                                    <button
                                        onClick={() => setShowMergeHelpModal(true)}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                                        title="머지 규칙 도움말"
                                    >
                                        <HelpCircle className="w-4 h-4" />
                                        도움말
                                    </button>
                                </div>
                                {/* 상세 설정 */}
                                <div className="flex-1 overflow-auto space-y-6">
                                    {/* Primary Key - 다중 선택 */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                                            🔗 연결 기준 (PK)
                                            <span className="text-xs font-normal text-gray-800">필수</span>
                                            <span
                                                className="text-gray-400 hover:text-gray-600 cursor-help"
                                                title="이 값이 같은 문서는 '같은 건'으로 인식되어 하나의 행으로 합쳐집니다.\n예: Invoice No, 차대번호, Description"
                                            >
                                                <HelpCircle className="w-3.5 h-3.5" />
                                            </span>
                                        </label>
                                        <p className="text-xs text-gray-500 mb-3">
                                            예: Invoice No가 같으면 같은 거래, 차대번호가 같으면 같은 차량
                                        </p>

                                        {/* PK 목록 */}
                                        <div className="space-y-2 mb-2">
                                            {(editMergeRules[0]?.primaryKeys || [editMergeRules[0]?.mainIdentifier]).filter(Boolean).map((pk, pkIdx) => (
                                                <div key={pkIdx} className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-400 w-6">{pkIdx + 1}.</span>
                                                    <select
                                                        value={pk || ''}
                                                        onChange={(e) => {
                                                            const updated = editMergeRules.length > 0 ? [...editMergeRules] : [{ primaryKeys: [], matchKey: '', fieldMappings: [] }];
                                                            const pks = updated[0].primaryKeys || [updated[0].mainIdentifier].filter(Boolean);
                                                            pks[pkIdx] = e.target.value;
                                                            updated[0] = { ...updated[0], primaryKeys: pks, mainIdentifier: pks[0] || '' };
                                                            setEditMergeRules(updated);
                                                            setIsEditing(true);
                                                        }}
                                                        className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-600 focus:border-gray-600 bg-white"
                                                    >
                                                        <option value="">필드 선택...</option>
                                                        {editFields.map(f => (
                                                            <option key={f.key} value={f.key}>{f.displayName || f.key}</option>
                                                        ))}
                                                    </select>
                                                    {pkIdx > 0 && (
                                                        <button
                                                            onClick={() => {
                                                                const updated = [...editMergeRules];
                                                                const pks = (updated[0].primaryKeys || []).filter((_, i) => i !== pkIdx);
                                                                updated[0] = { ...updated[0], primaryKeys: pks, mainIdentifier: pks[0] || '' };
                                                                setEditMergeRules(updated);
                                                                setIsEditing(true);
                                                            }}
                                                            className="p-2 text-gray-400 hover:text-red-500"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}

                                            {/* 첫 PK가 비어있으면 추가 버튼 대신 안내 */}
                                            {(editMergeRules[0]?.primaryKeys || [editMergeRules[0]?.mainIdentifier]).filter(Boolean).length === 0 && (
                                                <select
                                                    value=""
                                                    onChange={(e) => {
                                                        if (!e.target.value) return;
                                                        const updated = editMergeRules.length > 0 ? [...editMergeRules] : [{ primaryKeys: [], matchKey: '', fieldMappings: [] }];
                                                        updated[0] = { ...updated[0], primaryKeys: [e.target.value], mainIdentifier: e.target.value };
                                                        setEditMergeRules(updated);
                                                        setIsEditing(true);
                                                    }}
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-500"
                                                >
                                                    <option value="">PK 필드 선택...</option>
                                                    {editFields.map(f => (
                                                        <option key={f.key} value={f.key}>{f.displayName || f.key}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>

                                        {/* PK 추가 버튼 */}
                                        {(editMergeRules[0]?.primaryKeys || [editMergeRules[0]?.mainIdentifier]).filter(Boolean).length > 0 && (
                                            <button
                                                onClick={() => {
                                                    const updated = editMergeRules.length > 0 ? [...editMergeRules] : [{ primaryKeys: [], matchKey: '', fieldMappings: [] }];
                                                    const pks = updated[0].primaryKeys || [updated[0].mainIdentifier].filter(Boolean);
                                                    pks.push('');
                                                    updated[0] = { ...updated[0], primaryKeys: pks };
                                                    setEditMergeRules(updated);
                                                    setIsEditing(true);
                                                }}
                                                className="text-xs px-3 py-1.5 text-gray-800 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                                            >
                                                + 보조 PK 추가 (계층적 탐색)
                                            </button>
                                        )}

                                        {/* 다중 PK 설명 */}
                                        {(editMergeRules[0]?.primaryKeys || []).length > 1 && (
                                            <p className="text-xs text-gray-800 mt-2 bg-gray-100 p-2 rounded-lg">
                                                💡 계층적 PK: 1순위 PK가 없으면 2순위 PK로 그룹핑
                                            </p>
                                        )}
                                    </div>

                                    {/* 구분선 */}
                                    <div className="border-t border-gray-200"></div>

                                    {/* 매칭 키 (Orphan 연결용) */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                                            🔗 보조 연결 (Match Key)
                                            <span
                                                className="text-gray-400 hover:text-gray-600 cursor-help"
                                                title="연결 기준 필드가 없는 문서(인감증명 등)를 이 필드 값으로 연결합니다.\n예: 말소증의 '소유자'와 인감증명의 '성명'이 같으면 연결"
                                            >
                                                <HelpCircle className="w-3.5 h-3.5" />
                                            </span>
                                        </label>
                                        <p className="text-xs text-gray-500 mb-2">
                                            다른 용어, 같은 의미 연결. 예: 말소증 '성명(명칭)' = 인감증명 '성명'
                                        </p>
                                        <select
                                            value={editMergeRules[0]?.matchKey || ''}
                                            onChange={(e) => {
                                                const updated = editMergeRules.length > 0 ? [...editMergeRules] : [{ primaryKeys: [], matchKey: '', fieldMappings: [] }];
                                                updated[0] = { ...updated[0], matchKey: e.target.value };
                                                setEditMergeRules(updated);
                                                setIsEditing(true);
                                            }}
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-600 focus:border-gray-600 bg-white"
                                        >
                                            <option value="">선택 안함</option>
                                            {editFields.map(f => (
                                                <option key={f.key} value={f.key}>{f.displayName || f.key}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* 요약 정보 */}
                                    {((editMergeRules[0]?.primaryKeys || [editMergeRules[0]?.mainIdentifier]).filter(Boolean).length > 0 || editMergeRules[0]?.matchKey) && (
                                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                            <p className="text-sm font-medium text-gray-700 mb-2">📊 병합 규칙 요약</p>
                                            <ul className="text-xs text-gray-600 space-y-1">
                                                {(editMergeRules[0]?.primaryKeys || [editMergeRules[0]?.mainIdentifier]).filter(Boolean).length > 0 && (
                                                    <li>
                                                        <span className="text-gray-800 font-medium">PK</span>: {(editMergeRules[0]?.primaryKeys || [editMergeRules[0]?.mainIdentifier]).filter(Boolean).join(' → ')}
                                                        <span className="text-gray-400 ml-1">(같은 값 = 같은 행)</span>
                                                    </li>
                                                )}
                                                {editMergeRules[0]?.matchKey && (
                                                    <li>
                                                        <span className="text-green-600 font-medium">Match</span>: {editMergeRules[0].matchKey}
                                                        <span className="text-gray-400 ml-1">(PK 없는 문서 연결)</span>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    )}

                                    {/* 고급 설정 (접기) */}
                                    <details className="border border-gray-200 rounded-lg">
                                        <summary className="px-4 py-3 text-sm text-gray-600 cursor-pointer hover:bg-gray-50">
                                            ⚙️ 고급 설정 (필드 매핑)
                                        </summary>
                                        <div className="px-4 pb-4 pt-2">
                                            <div className="flex items-center justify-between mb-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-900">필드 매핑</label>
                                                    <p className="text-xs text-gray-500">필드명이 다를 때 수동 연결</p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        const updated = editMergeRules.length > 0 ? [...editMergeRules] : [{ primaryKeys: [], matchKey: '', fieldMappings: [] }];
                                                        updated[0] = {
                                                            ...updated[0],
                                                            fieldMappings: [...(updated[0].fieldMappings || []), { sourceField: '', targetField: '', mode: 'fillEmpty' }]
                                                        };
                                                        setEditMergeRules(updated);
                                                        setIsEditing(true);
                                                    }}
                                                    className="text-xs px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                                >
                                                    + 추가
                                                </button>
                                            </div>
                                            {(editMergeRules[0]?.fieldMappings || []).length === 0 ? (
                                                <div className="text-sm text-gray-400 text-center py-4">
                                                    필드명이 같으면 자동으로 매핑됩니다
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {(editMergeRules[0]?.fieldMappings || []).map((mapping, mapIdx) => (
                                                        <div key={mapIdx} className="flex items-center gap-2">
                                                            <select
                                                                value={mapping.sourceField || ''}
                                                                onChange={(e) => {
                                                                    const updated = [...editMergeRules];
                                                                    updated[0].fieldMappings[mapIdx].sourceField = e.target.value;
                                                                    setEditMergeRules(updated);
                                                                    setIsEditing(true);
                                                                }}
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                            >
                                                                <option value="">소스 필드</option>
                                                                {editFields.map(f => (
                                                                    <option key={f.key} value={f.key}>{f.displayName || f.key}</option>
                                                                ))}
                                                            </select>
                                                            <span className="text-gray-400">→</span>
                                                            <select
                                                                value={mapping.targetField || ''}
                                                                onChange={(e) => {
                                                                    const updated = [...editMergeRules];
                                                                    updated[0].fieldMappings[mapIdx].targetField = e.target.value;
                                                                    setEditMergeRules(updated);
                                                                    setIsEditing(true);
                                                                }}
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                            >
                                                                <option value="">타겟 필드</option>
                                                                {editFields.map(f => (
                                                                    <option key={f.key} value={f.key}>{f.displayName || f.key}</option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                onClick={() => {
                                                                    const updated = [...editMergeRules];
                                                                    updated[0].fieldMappings = updated[0].fieldMappings.filter((_, i) => i !== mapIdx);
                                                                    setEditMergeRules(updated);
                                                                    setIsEditing(true);
                                                                }}
                                                                className="p-2 text-gray-400 hover:text-gray-600"
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </details>
                                </div>

                                {/* 저장 버튼 */}
                                {isEditing && (
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                                        <button
                                            onClick={handleSave}
                                            disabled={loading}
                                            className="flex items-center gap-1.5 px-5 py-2.5 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
                                        >
                                            <Save className="w-4 h-4" />
                                            {loading ? '저장 중...' : '저장'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* 팝업 에디터 (전체화면 오버레이) */}
            {isPopupOpen && (
                <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* 팝업 헤더 */}
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-100 rounded-lg">
                                    <Edit3 className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">사용자 정의 프롬프트 편집</h2>
                                    <p className="text-xs text-gray-500">전체 화면에서 편하게 수정하세요.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setIsPopupOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* 팝업 에디터 영역 */}
                        <div className="flex-1 p-0 relative bg-white">
                            <textarea
                                value={customPrompt}
                                onChange={(e) => { setCustomPrompt(e.target.value); setIsEditing(true); }}
                                className="w-full h-full p-6 text-base font-mono leading-relaxed text-gray-800 bg-white resize-none focus:outline-none focus:ring-0"
                                placeholder="여기에 프롬프트를 입력하세요..."
                                spellCheck="false"
                            />
                        </div>

                        {/* 팝업 푸터 */}
                        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <div className="text-xs text-gray-500 flex items-center gap-4">
                                <span>Lines: {customPrompt.split('\n').length}</span>
                                <span>Length: {customPrompt.length} chars</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setIsPopupOpen(false)}
                                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg text-sm hover:bg-gray-50"
                                >
                                    닫기
                                </button>
                                <button
                                    onClick={() => { setIsPopupOpen(false); handleSave(); }}
                                    className="px-4 py-2 bg-gray-800 text-white font-bold rounded-lg text-sm hover:bg-gray-900 shadow-sm flex items-center gap-2"
                                >
                                    <Save className="w-4 h-4" />
                                    저장하고 닫기
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Meta-Prompt 팝업 (읽기 전용) */}
            {isMetaPromptPopupOpen && (
                <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* 팝업 헤더 */}
                        <div className="px-4 py-3 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="bg-gray-200 text-gray-900 px-2 py-1 rounded text-xs font-bold">Auto</span>
                                <h2 className="text-sm font-bold text-gray-900">자동 생성된 Meta-Prompt</h2>
                                <span className="text-xs text-gray-400">(읽기 전용)</span>
                            </div>
                            <button
                                onClick={() => setIsMetaPromptPopupOpen(false)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Meta-Prompt 내용 */}
                        <div className="flex-1 p-4 overflow-auto bg-white">
                            <pre className="w-full h-full p-4 text-sm font-mono leading-relaxed text-gray-800 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap">
                                {previewPrompt || '필드 정보를 입력하면 자동으로 프롬프트가 생성됩니다.'}
                            </pre>
                        </div>

                        {/* 팝업 푸터 */}
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <div className="text-xs text-gray-500 flex items-center gap-4">
                                <span>Lines: {previewPrompt?.split('\n').length || 0}</span>
                                <span>Length: {previewPrompt?.length || 0} chars</span>
                            </div>
                            <button
                                onClick={() => setIsMetaPromptPopupOpen(false)}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 font-medium rounded text-xs hover:bg-gray-300"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ⭐ PDF 페이지 선택 모달 */}
            {showPageSelectionModal && <PageSelectionModal />}

            {/* 🏷️ OCR 라벨링 모달 */}
            {showLabelingModal && (
                <OCRLabelingModal
                    templateId={selectedTemplateId}
                    templateName={editName}
                    imageUrl={labelingSampleImageUrl}
                    fileName={labelingSampleFileName} // 🏷️ 파일명 전달
                    onClose={() => setShowLabelingModal(false)}
                    onSave={(labels) => {
                        console.log('라벨 저장됨:', labels);
                        // 창 닫지 않음 (User Request)
                        // 로컬 상태 즉시 반영 (리로드 없이 라벨 데이터 유지)
                        setTemplates(prev => prev.map(t =>
                            t.id === selectedTemplateId
                                ? { ...t, labels: labels }
                                : t
                        ));
                    }}
                    totalPages={totalPdfPages || 1}
                    currentPage={1}
                    apiBaseUrl={apiBaseUrl}
                    templateFields={editFields || []}
                />
            )}

            {/* Toast 알림 */}
            {toast.show && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded shadow-lg text-sm z-50 flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Check className="w-4 h-4 text-green-400" />
                    {toast.message}
                </div>
            )}

            {/* 도움말 모달 */}
            {showHelpModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100]" onClick={() => setShowHelpModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* 헤더 - 심플 */}
                        <div className="border-b px-5 py-4 flex items-center justify-between">
                            <h3 className="text-base font-medium text-gray-900">템플릿 관리 가이드</h3>
                            <button onClick={() => setShowHelpModal(false)} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* 콘텐츠 */}
                        <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)] space-y-6">
                            {/* 탭 설명 */}
                            <div>
                                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-gray-200 text-gray-800 rounded-full flex items-center justify-center text-xs">1</span>
                                    탭 구조
                                </h4>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <div className="text-2xl mb-1">📋</div>
                                        <div className="font-medium text-sm">필드 설정</div>
                                        <div className="text-xs text-gray-500">추출할 필드 정의</div>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <div className="text-2xl mb-1">✨</div>
                                        <div className="font-medium text-sm">프롬프트</div>
                                        <div className="text-xs text-gray-500">AI 프롬프트 수정</div>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <div className="text-2xl mb-1">🔄</div>
                                        <div className="font-medium text-sm">머지 규칙</div>
                                        <div className="text-xs text-gray-500">문서 간 병합 설정</div>
                                    </div>
                                </div>
                            </div>

                            {/* 머지 규칙 아이콘 */}
                            <div>
                                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-gray-200 text-gray-800 rounded-full flex items-center justify-center text-xs">2</span>
                                    머지 규칙 아이콘
                                </h4>
                                <div className="grid grid-cols-5 gap-2">
                                    <div className="text-center p-2 bg-gray-100 rounded">
                                        <div className="text-xl">🔵</div>
                                        <div className="text-[10px] text-gray-600">메인 식별</div>
                                    </div>
                                    <div className="text-center p-2 bg-gray-50 rounded">
                                        <div className="text-xl">🟡</div>
                                        <div className="text-[10px] text-gray-600">서브 식별</div>
                                    </div>
                                    <div className="text-center p-2 bg-purple-50 rounded">
                                        <div className="text-xl">🔗</div>
                                        <div className="text-[10px] text-gray-600">매칭 키</div>
                                    </div>
                                    <div className="text-center p-2 bg-gray-50 rounded">
                                        <div className="text-xl">↗️</div>
                                        <div className="text-[10px] text-gray-600">값 보냄</div>
                                    </div>
                                    <div className="text-center p-2 bg-green-50 rounded">
                                        <div className="text-xl">↙️</div>
                                        <div className="text-[10px] text-gray-600">값 받음</div>
                                    </div>
                                </div>
                            </div>

                            {/* 예시 1 */}
                            <div>
                                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-gray-200 text-gray-800 rounded-full flex items-center justify-center text-xs">3</span>
                                    예시: 말소증 + 주민증
                                </h4>
                                <div className="bg-gradient-to-r from-gray-100 to-yellow-50 rounded-lg p-4 text-sm">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="font-medium text-gray-900 mb-1">🔵 메인 (말소증)</div>
                                            <div className="text-xs text-gray-600">메인 식별: <code className="bg-white px-1 rounded">차대번호</code></div>
                                        </div>
                                        <div>
                                            <div className="font-medium text-yellow-700 mb-1">🟡 서브 (주민증)</div>
                                            <div className="text-xs text-gray-600">서브 식별: <code className="bg-white px-1 rounded">주민등록번호</code></div>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-gray-200 text-xs">
                                        <span className="text-purple-600 font-medium">🔗 매칭 키: 성명</span>
                                        <span className="mx-2">|</span>
                                        <span className="text-gray-600">주민등록번호 → 생년월일</span>
                                    </div>
                                </div>
                            </div>

                            {/* 예시 2 */}
                            <div>
                                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-gray-200 text-gray-800 rounded-full flex items-center justify-center text-xs">4</span>
                                    예시: Invoice + Packing
                                </h4>
                                <div className="bg-gradient-to-r from-green-50 to-orange-50 rounded-lg p-4 text-sm">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="font-medium text-green-700 mb-1">🔵 메인 (Invoice)</div>
                                            <div className="text-xs text-gray-600">식별: <code className="bg-white px-1 rounded">수량,단가,금액</code></div>
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-700 mb-1">🟡 서브 (Packing)</div>
                                            <div className="text-xs text-gray-600">식별: <code className="bg-white px-1 rounded">net_weight</code></div>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-gray-200 text-xs">
                                        <span className="text-purple-600 font-medium">🔗 매칭 키: invoice_no</span>
                                        <span className="mx-2">|</span>
                                        <span className="text-gray-600">net_weight → net_weight [빈칸채움]</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 푸터 */}
                        <div className="px-6 py-3 bg-gray-50 border-t flex justify-between items-center">
                            <span className="text-xs text-gray-500">자세한 가이드: docs/template_merge_guide.md</span>
                            <button
                                onClick={() => setShowHelpModal(false)}
                                className="px-4 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔄 머지 역할 선택 팝업 - PK 기반 */}
            {mergeRolePopup.show && (
                <div
                    className="fixed inset-0 z-[200]"
                    onClick={() => setMergeRolePopup({ show: false, fieldKey: null, x: 0, y: 0 })}
                >
                    <div
                        className="absolute bg-white rounded-lg shadow-xl border border-gray-200 py-1.5 min-w-[220px]"
                        style={{
                            left: Math.min(mergeRolePopup.x, window.innerWidth - 240),
                            top: Math.min(mergeRolePopup.y, window.innerHeight - 200)
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="px-2.5 py-1.5 border-b bg-gray-50">
                            <div className="text-[11px] font-bold text-gray-700">"{mergeRolePopup.fieldKey}" 역할</div>
                        </div>
                        <div className="py-0.5">
                            {[
                                {
                                    role: 'none',
                                    icon: '⚪',
                                    color: 'bg-gray-300',
                                    label: '없음',
                                    desc: '머지 규칙에 사용 안 함'
                                },
                                {
                                    role: 'main',
                                    icon: '🔑',
                                    color: 'bg-gray-600',
                                    label: 'Primary Key (PK)',
                                    desc: '같은 값 = 같은 행으로 병합 (예: 차대번호)'
                                },
                                {
                                    role: 'match',
                                    icon: '🔗',
                                    color: 'bg-green-500',
                                    label: 'Match Key',
                                    desc: 'PK 없는 문서 연결 (예: 신분증의 성명)'
                                }
                            ].map(item => {
                                const currentRole = getFieldMergeRole(mergeRolePopup.fieldKey);
                                const isSelected = currentRole === item.role;
                                return (
                                    <button
                                        key={item.role}
                                        onClick={() => setFieldMergeRole(mergeRolePopup.fieldKey, item.role)}
                                        className={`w-full px-2.5 py-2.5 text-left hover:bg-gray-100 flex items-start gap-2.5 ${isSelected ? 'bg-gray-100' : ''}`}
                                    >
                                        <span className={`w-4 h-4 rounded-full ${item.color} mt-0.5 shrink-0 flex items-center justify-center text-[10px]`}>
                                            {item.icon === '⚪' ? '' : ''}
                                        </span>
                                        <div className="flex-1">
                                            <div className={`text-xs font-bold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                                {item.icon} {item.label}{isSelected && ' ✓'}
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-0.5">{item.desc}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="px-2.5 py-1.5 border-t bg-gray-100">
                            <div className="text-[9px] text-gray-900">
                                <strong>💡 Tip:</strong> 3. 머지 규칙 탭에서 다중 PK 설정 가능
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 📄 머지 규칙 도움말 모달 - Minimal Style */}
            {showMergeHelpModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-lg shadow-xl w-[640px] max-h-[80vh] overflow-hidden flex flex-col">
                        {/* 헤더 */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-base font-medium text-gray-900">머지 규칙 도움말</h2>
                            <button
                                onClick={() => setShowMergeHelpModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* 내용 */}
                        <div className="flex-1 overflow-auto px-6 py-5 space-y-6 text-sm text-gray-700">

                            {/* 머지란? */}
                            <div>
                                <h3 className="font-medium text-gray-900 mb-2">머지(Merge)란?</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    여러 페이지에서 추출된 데이터를 <strong className="text-gray-900">연결 기준 필드</strong>를 기준으로
                                    하나의 행으로 병합합니다.
                                </p>
                            </div>

                            <hr className="border-gray-100" />

                            {/* 예시 1 */}
                            <div>
                                <h3 className="font-medium text-gray-900 mb-3">예시: Invoice + Packing List</h3>
                                <div className="bg-gray-50 rounded border border-gray-200 p-4 font-mono text-xs space-y-2">
                                    <div className="text-gray-500"># Page 1 - Invoice</div>
                                    <div>Invoice No: <strong className="text-gray-900">INV-2024-001</strong></div>
                                    <div>Item: ENGINE PARTS, Unit Price: $150</div>
                                    <div className="pt-2 text-gray-500"># Page 2 - Packing List</div>
                                    <div>Invoice No: <strong className="text-gray-900">INV-2024-001</strong></div>
                                    <div>N/W: 25.5 KG, G/W: 28.0 KG</div>
                                    <hr className="border-gray-200 my-2" />
                                    <div className="text-gray-500"># 결과 (연결 기준: Invoice No)</div>
                                    <div className="text-gray-900">→ 동일한 Invoice No를 가진 두 페이지가 하나의 행으로 병합</div>
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* 설정 항목 */}
                            <div>
                                <h3 className="font-medium text-gray-900 mb-3">설정 항목</h3>
                                <div className="space-y-3">
                                    <div className="flex gap-3">
                                        <div className="w-24 shrink-0 text-gray-500">연결 기준</div>
                                        <div>이 값이 같으면 = 같은 건. 예: Invoice No, 차대번호, Description</div>
                                    </div>
                                    <div className="flex gap-3">
                                        <div className="w-24 shrink-0 text-gray-500">보조 연결</div>
                                        <div>다른 용어, 같은 의미 연결. 예: A문서 '성명(명칭)' = B문서 '성명'</div>
                                    </div>
                                    <div className="flex gap-3">
                                        <div className="w-24 shrink-0 text-gray-500">복수 연결</div>
                                        <div>여러 개 지정 시, 첫 번째로 값이 있는 필드를 사용</div>
                                    </div>
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* 보조 연결 예시 */}
                            <div>
                                <h3 className="font-medium text-gray-900 mb-3">보조 연결 활용</h3>
                                <div className="bg-gray-50 rounded border border-gray-200 p-4 font-mono text-xs space-y-1">
                                    <div className="text-gray-500"># 차량등록증 (연결 기준: 차대번호)</div>
                                    <div>차대번호: KMHST81VP..., 소유자: 홍길동</div>
                                    <div className="pt-2 text-gray-500"># 인감증명서 (연결 기준 없음)</div>
                                    <div>성명: 홍길동, 주민번호: 850101-*******</div>
                                    <hr className="border-gray-200 my-2" />
                                    <div className="text-gray-900">→ 보조 연결: "소유자" = "성명" → 병합</div>
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* 매칭 품질 표시 */}
                            <div>
                                <h3 className="font-medium text-gray-900 mb-3">연결 품질 표시</h3>
                                <div className="space-y-2 text-xs">
                                    <div className="flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">정확</span>
                                        <span className="text-gray-600">값이 100% 일치 (예: INV-001 = INV-001)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 bg-gray-200 text-gray-900 rounded">~</span>
                                        <span className="text-gray-600">정규화 일치 - 대소문자, 공백, 특수문자 무시 (예: INV-001 = inv 001)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-bold">⚠ 부분</span>
                                        <span className="text-gray-600">부분 일치 - 확인 필요 (예: KMHST81VP12345 ↔ ST81VP12345)</span>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* 푸터 */}
                        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
                            <button
                                onClick={() => setShowMergeHelpModal(false)}
                                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-800"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔍 EXAMPLE 확대 편집 모달 */}
            {exampleExpandModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setExampleExpandModal(prev => ({ ...prev, isOpen: false }))}>
                    <div
                        className="bg-white rounded-lg shadow-2xl w-[520px] max-h-[70vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* 헤더 */}
                        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b">
                            <div>
                                <h3 className="font-semibold text-sm text-gray-800">EXAMPLE 편집</h3>
                                <p className="text-[11px] text-gray-500 mt-0.5">
                                    <span className="font-mono text-blue-600">{exampleExpandModal.fieldKey}</span>
                                    {exampleExpandModal.fieldType === 'table' && <span className="ml-1 text-gray-400">(테이블 컬럼)</span>}
                                </p>
                            </div>
                            <button
                                onClick={() => setExampleExpandModal(prev => ({ ...prev, isOpen: false }))}
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* 바디 */}
                        <div className="p-4">
                            <textarea
                                value={exampleExpandModal.value}
                                onChange={(e) => setExampleExpandModal(prev => ({ ...prev, value: e.target.value }))}
                                className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-y"
                                placeholder="예시 값을 입력하세요... (여러 줄 가능: 값1 | 값2 | 값3)"
                                autoFocus
                            />
                            <p className="mt-1.5 text-[11px] text-gray-400">
                                여러 예시는 <span className="font-mono bg-gray-100 px-1 rounded">|</span> 로 구분하거나 줄바꿈으로 입력 ({exampleExpandModal.value.length}자)
                            </p>
                        </div>

                        {/* 푸터 */}
                        <div className="flex items-center justify-end gap-2 px-4 py-2.5 bg-gray-50 border-t">
                            <button
                                onClick={() => setExampleExpandModal(prev => ({ ...prev, isOpen: false }))}
                                className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
                            >
                                취소
                            </button>
                            <button
                                onClick={() => {
                                    const val = exampleExpandModal.value;
                                    if (exampleExpandModal.fieldType === 'single') {
                                        setExampleValues(prev => ({ ...prev, [exampleExpandModal.fieldKey]: val }));
                                    } else if (exampleExpandModal.fieldType === 'table') {
                                        updateTableColumn(exampleExpandModal.tableIdx, exampleExpandModal.colIdx, 'example', val);
                                    }
                                    setIsEditing(true);
                                    setExampleExpandModal(prev => ({ ...prev, isOpen: false }));
                                }}
                                className="px-4 py-1.5 text-xs font-medium text-white bg-gray-900 rounded hover:bg-gray-800"
                            >
                                적용
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

